/* eslint-disable @next/next/no-img-element */
import { faEnvira } from "@fortawesome/free-brands-svg-icons/faEnvira";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faAppleWhole } from "@fortawesome/pro-solid-svg-icons/faAppleWhole";
import { faBars } from "@fortawesome/pro-solid-svg-icons/faBars";
import { faBedFront } from "@fortawesome/pro-solid-svg-icons/faBedFront";
import { faDog } from "@fortawesome/pro-solid-svg-icons/faDog";
import { faDroplet } from "@fortawesome/pro-solid-svg-icons/faDroplet";
import { faEarthAmericas } from "@fortawesome/pro-solid-svg-icons/faEarthAmericas";
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { faJugBottle } from "@fortawesome/pro-solid-svg-icons/faJugBottle";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons/faRotateRight";
import { faTreeDeciduous } from "@fortawesome/pro-solid-svg-icons/faTreeDeciduous";
import { faX } from "@fortawesome/pro-solid-svg-icons/faX";
import { faArrowLeftLong } from "@fortawesome/pro-thin-svg-icons/faArrowLeftLong";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, AccordionItem, ItemState } from "@szhsin/react-accordion";
import clsx from "clsx";
import DOMPurify from "dompurify";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { useFormatter } from "next-intl";
import NextImage from "next/image";
import { useRouter } from "next/router";
import {
  forwardRef,
  Fragment,
  HTMLAttributes,
  PropsWithChildren,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import ReactModal from "react-modal";
import { useTranslations } from "use-intl";
import hgfPersonTree from "../../../public/hgf_pub_trees.svg";
import Carousel from "../../../src/Carousel/Carousel";
import Button from "../../../src/components/Button/Button";
import ProjectStandardImage from "../../../src/components/image/ProjectStandardImage";
import SdgImage from "../../../src/components/image/SdgImage";
import SkeletonImage from "../../../src/components/image/SkeletonImage";
import { MapBoxProvider } from "../../../src/components/MapBox/MapBoxContext";
import MapBoxMapView from "../../../src/components/MapBox/MapBoxMapView";
import BaseModal from "../../../src/components/modal/BaseModal";
import SocialMediaIcon from "../../../src/components/profile/SocialMediaIcon";
import Table, { RowType } from "../../../src/components/table/Table";
import { TablePagination } from "../../../src/components/table/TablePagination";
import { useGetPublicCo2OffsettingQuery } from "../../../src/redux/apiSlice";
import { FormatOptions, SocialMedia, SocialMediaObj } from "../../../src/util";
import { getMessages } from "../../../src/util/messages";
import prisma from "../../../src/util/prisma";
import { SelectProjectForMap } from "../../../src/util/prismaHelper";
import { getPublicPageInfo } from "../../../src/util/utilPublicPageInfo";
import styles from "./uebernachtung.module.scss";

type DigitalProductPassInformation = {
  publicId?: string;
  heroSectionImages: { src: string; alt?: string }[];
  product: {
    producerName: string;
    name: string;
    title: string;
    description: string;
    points: {
      header: string;
      text: string;
    }[];
  };
  oeko_plague: null | {
    text: string;
    icon: string;
  };
  ingredients?: {
    text: string;
  };
  nutritionalValues?: {
    text: string;
  };
  additionalInformation: {
    pros: string[];
    documents: {
      name: string;
      url?: string;
      fileId?: string;
    }[];
  };
  productImages: string[];
  cta?: {
    url: string;
    text?: string;
  };
  supplyChain: {
    pos: number;
    name: string;
    imageUrl: string | null;
    address: {
      name: string | null;
      street: string;
      postalCode: string;
      city: string;
    };
    link: string | null;
  }[];
  co2Footprint: {
    header?: string;
    text?: string;
    offset?: string;
    offsetRed?: boolean;
    offsetUnit?: string;
    offsetHint?: string;
    list?: {
      header?: string;
      items: string[];
    };
    additionalTexts?: {
      header?: string;
      text: string;
    }[];
    documents: {
      name: string;
      url?: string;
      fileId?: string;
    }[];
  };
  co2Offset?: {
    text: string;
  };
  sustainability: {
    product: {
      text: string;
      additionalTexts?: { header: string; text: string; icon?: string }[];
    };
    producer: {
      text: string | null;
      additionalTexts?: { header: string; text: string; icon?: string }[];
    };
    esg?: {
      e: string[] | string;
      s: string[] | string;
      g: string[] | string;
    };
  };
  sdgs: {
    id: string;
    infoText: string | null;
    videoUrl: string | null;
    imageUrl: string | null;
  }[];
  awards?: {
    imageUrl: string;
    name?: string;
  }[];
  memberships?: {
    imageUrl: string;
    name?: string;
  }[];
  producerInfo: {
    text: string;
    imageUrl: string | null;
    imageAlt?: string;
    contact: {
      name: string;
      street: string;
      postalCode: string;
      city: string;
      phone: string;
      email: string;
      web: string;
      shopUrl?: string;
    };
  };
  social?: Partial<Record<SocialMedia, SocialMediaObj>> | null;
};

const ConcordeUebernachtungDPPInfos: DigitalProductPassInformation = {
  publicId: "hotel-concorde",
  heroSectionImages: [
    {
      src: "/hotel-concorde/Designzimmer-1.jpg",
      alt: "Design Zimmer im Hotel Concorde",
    },
    {
      src: "/hotel-concorde/Designzimmer-2.jpg",
      alt: "Design Zimmer im Hotel Concorde",
    },
    {
      src: "/hotel-concorde/Designzimmer-3.jpg",
      alt: "Design Zimmer im Hotel Concorde",
    },
  ],
  product: {
    producerName: "Hotel Concorde",
    name: "Ihr Aufenthalt",
    title: "Übernachtung im Hotel Concorde",
    description: `Die Übernachtung im Hotel Concorde umfasst die Nutzung eines Hotelzimmers im familiengeführten Business-Boutique-Hotel in Bad Soden am Taunus.  Die gesamte Dienstleistung ist klimabilanziert nach dem GHG-Protokoll (Market-based approach) und eingebettet in ein umfassendes Nachhaltigkeitskonzept, das sich an den freiwilligen EU-Standards gemäß VSME-C sowie an ausgewählten Anforderungen der verbindlichen EU-Richtlinie CSRD und deren technischen Standards (ESRS) orientiert.

Das Hotel verbindet ökologisches Handeln mit sozialer Verantwortung und verantwortungsvoller Unternehmensführung.

Die Gäste des Hotel Concorde profitieren von einem komfortablen und dennoch klimabewussten Aufenthalt. Eine transparente Dokumentation der ökologischen und sozialen Wirkung ist jederzeit einsehbar.`,
    points: [
      {
        header: "Zimmer",
        text: "Einzel-, Doppel- oder Business-Zimmer",
      },
      {
        header: "Ausstattung",
        text: "Eigenes Bad mit Dusche oder Badewanne, Schreibtisch, WLAN, TV, Klimaregulierung per Wärmepumpe, schallisolierte Fenster (Dreifachverglasung)",
      },
      {
        header: "Frühstück (optional)",
        text: "Regionale, biologische Zutaten",
      },
      {
        header: "Lage",
        text: "Direkt gegenüber dem Bahnhof Bad Soden (Taunus)",
      },
      {
        header: "Check-in / Check-out",
        text: "Ab 14:00 Uhr / bis 11:00 Uhr",
      },
      {
        header: "Verkehrsanbindung",
        text: "Direkte ÖPNV-Verbindungen nach Frankfurt (Messe/Hbf) und Wiesbaden",
      },
    ],
  },
  oeko_plague: null,
  additionalInformation: {
    pros: [
      "Klimabilanz: 9,6 kg CO₂e pro Übernachtung",
      "Klimakompensation: 100 % CO₂-Ausgleich ab 06/2025 über zertifizierte Projekte",
      "Energieversorgung: Ökostrom & Wärmepumpentechnik für Heizung/Kühlung",
      "Regionale Herkunft & kurze Lieferwege",
      "Ressourcenschonung: Reduziertes Abfallaufkommen, keine Einwegverpackungen",
      "Reinigung & Pflege: Biologisch abbaubare Reinigungs- und Pflegeprodukte",
      "Nachhaltigkeitsmanagement: Code-of-Conduct & freiwillige CSRD-Berichterstattung",
    ],
    documents: [
      {
        name: "CO₂-Bilanz 2024",
        url: "/hotel-concorde/docs/CCF Hotel Concorde OneSheet 2024 2025-06-27.pdf",
      },
      {
        name: "Nachhaltigkeitsbericht 2024",
        url: "/hotel-concorde/docs/Nachhaltigkeitsbericht Hotel Concorde 2024 2025-06-26.pdf",
      },
    ],
  },
  productImages: [
    "/hotel-concorde/Designzimmer-1.jpg",
    "/hotel-concorde/Designzimmer-2.jpg",
    "/hotel-concorde/Designzimmer-3.jpg",
    "/hotel-concorde/Balkon-4.jpg",
    "/hotel-concorde/Fruehstueckraum-5.jpg",
  ],
  cta: {
    url: "https://ibe2.smarthotel.nl/hotel/9a68a7bd-4ff0-49b7-bd23-cd45a7122996/rooms",
    text: "Jetzt buchen",
  },
  supplyChain: [],
  co2Footprint: {
    // header: "Klimapositiv: Bindet mehr CO₂ als sie verursacht",
    text: `Die CO₂e-Emissionen einer Übernachtung im Hotel Concorde betragen 9,6 kg pro Gast und Nacht (Berechnungsgrundlage: Jahr 2024). Die Ermittlung erfolgt nach dem GHG-Protokoll (Market-based Approach) und umfasst Scope 1 bis 3.`,
    offset: "9,6 kg",
    offsetRed: true,
    offsetUnit: "CO₂e",
    offsetHint: "pro Gast / Nacht",
    list: {
      header: "Hauptquellen der Emissionen",
      items: [
        "Heizung (Erdgas & Wärmepumpe): 67,5 %",
        "Lebensmittel & Getränke: 12,6 %",
        "Fuhrpark: 8,5 %",
        "Wäschereinigung: 5,8 %",
      ],
    },
    additionalTexts: [
      {
        header: "CO₂-Reduktion seit dem Referenzjahr 2019",
        text: "Die größten Einsparungen wurden durch den Bezug von Ökostrom, die Umstellung auf LED-Technik, den Einsatz von Wärmepumpen und die Optimierung des Abfall- und Wäschemanagements erzielt.",
      },
    ],
    documents: [
      {
        name: "CO₂-Bilanz 2024",
        url: "/hotel-concorde/docs/CCF Hotel Concorde OneSheet 2024 2025-06-27.pdf",
      },
      {
        name: "Nachhaltigkeitsbericht 2024",
        url: "/hotel-concorde/docs/Nachhaltigkeitsbericht Hotel Concorde 2024 2025-06-26.pdf",
      },
    ],
  },
  co2Offset: {
    text: "Seit dem 1. Juli 2025 werden alle verbleibenden Emissionen pro Übernachtung im Hotel Concorde vollständig durch zertifizierte Klimaschutzprojekte ausgeglichen. Diese Projekte erfüllen höchste internationale Standards (z.B. Gold Standard) und werden regelmäßig überwacht.",
  },
  sustainability: {
    product: {
      text: "Der Aufenthalt im Hotel Concorde ist Teil eines ganzheitlich nachhaltigen Hotelbetriebs, der ökologische und soziale Verantwortung miteinander vereint. Alle Umweltauswirkungen der Dienstleistung sind bilanziert und transparent dokumentiert.",
      additionalTexts: [
        {
          header: "Klimaschutz & CO₂-Kompensation",
          text: `Jede Übernachtung ist vollständig klimabilanziert (9,6 kg CO₂e pro Gast). Seit dem 1. Juli 2025 wird der verbleibende ökologische Fußabdruck zu 100 % durch zertifizierte Klimaschutzprojekte ausgeglichen. Die Emissionen konnten seit 2019 um 57,9 % reduziert werden. Insbesondere der Bezug von Ökostrom sowie Investitionen in den vergangenen Jahren in Wärmepumpentechnik, LED-Beleuchtung und optimierte Gebäudetechnik wirken sich besonders positiv auf die Klimabilanz aus.`,
          icon: "globe",
        },
        {
          header: "Wasser & Ressourcenschonung",
          text: `Das Hotel spart aktiv Wasser: Der Verbrauch beträgt 194,5 Liter pro Gast und Übernachtung – eine Reduktion um 33,5 % gegenüber dem Referenzjahr 2019. Zusätzlich wird Mineralwasser für die Gäste direkt vor Ort aufbereitet, um Transportwege zu vermeiden.`,
          icon: "waterdroplet",
        },
        {
          header: "Verpackungsfreiheit & Müllvermeidung",
          text: "Einwegplastik wird konsequent vermieden – insbesondere im Frühstücks- und Hygiene-Bereich. Abfälle werden sortenrein getrennt und zu 51,5 % recycelt.",
          icon: "jugBottle",
        },
        {
          header: "Nachhaltiger Aufenthalt & Komfort",
          text: "Die Zimmer werden klimafreundlich über zentral gesteuerte Wärmepumpen beheizt und gekühlt. Gäste nutzen biologisch abbaubare und nachfüllbare Pflegeprodukte. Ein optionales Frühstück mit regionalen und biologischen Zutaten steht zur Verfügung. Alle Lieferwege werden bewusst kurz gehalten. Die Handtücher werden im Haus gereinigt, um Transportemissionen (Scope 3) zu vermeiden.",
          icon: "bedFront",
        },
        {
          header: "Hundefreundlich & sozial verträglich",
          text: "Besondere Zimmer sind auf die Mitreise von Hunden ausgerichtet. Lebensmittelüberschüsse aus dem Frühstück dürfen von Mitarbeitenden mitgenommen werden, um Abfälle zu vermeiden und soziale Teilhabe zu fördern.",
          icon: "dog",
        },
      ],
    },
    producer: {
      text: null,
      additionalTexts: [
        {
          header: "Nachhaltigkeit als Leitprinzip",
          icon: "heart",
          text: `Nachhaltigkeit ist im Hotel Concorde fest in der Unternehmenskultur verankert. Als privat geführtes Familienhotel in fünfter Generation setzt das Haus auf Energieeffizienz, respektvollen Umgang mit Mensch und Natur sowie transparente Prozesse. Gäste, Partner und Mitarbeitende sind aktiv in den Transformationsprozess eingebunden.

Das Concorde Hotel versteht Nachhaltigkeit als Verbindung von Tradition, Verantwortung und zukunftsorientiertem Handeln.`,
        },
        {
          header: "ESG beim Hotel Concorde",
          icon: "leaf",
          text: `Nachhaltigkeit wird im Hotel Concorde ganzheitlich verstanden – über Umwelt, Soziales und Unternehmensführung hinweg. 

Das Nachhaltigkeitsmanagement orientiert sich an den international anerkannten ESG-Kriterien sowie an der EU-Richtlinie CSRD. Es wird regelmäßig überprüft und weiterentwickelt, um den Anforderungen an eine verantwortungsvolle Hotelführung gerecht zu werden.`,
        },
      ],
    },
    esg: {
      e: [
        "Klimabilanzierung (GHG-Protokoll Scope 1–3)",
        "100 % Ökostrom",
        "Wärmepumpentechnik für Heizung/Kühlung",
        "Plastikvermeidung seit 2012",
        "Biologisch abbaubare Reinigungs- und Pflegeprodukte",
        "Wasserreduktion und Abfalltrennung",
      ],
      s: [
        "Team aus 7 Nationalitäten",
        "Tägliches gemeinsames Mitarbeitenden-Frühstück",
        "Geringe Fluktuation, hohe Betriebszugehörigkeit",
        "Zusatzleistungen (Jobticket, Altersvorsorge etc.)",
        "Datenschutzkonzept für Gäste und Personal",
      ],
      g: [
        "Code of Conduct für Mitarbeitende und Lieferanten",
        "Freiwillige Nachhaltigkeitsberichtserstattung nach CSRD",
        "Transparente Kommunikation & Feedbackkultur",
        "Mitglied DEHOGA, externe Beratung (Genesys)",
        "Null-Toleranz-Politik gegenüber Korruption",
      ],
    },
  },
  sdgs: [
    {
      id: "6",
      infoText:
        "Sparsame Wassernutzung (Dusche, WC), optimierte Wäscheprozesse, Gartenbewässerung",
      videoUrl: null,
      imageUrl: null,
    },
    {
      id: "8",
      infoText:
        "Nachhaltigkeit als regionaler Wirtschaftsfaktor, Gesundheitsschutz, faire Arbeitsbedingungen",
      videoUrl: null,
      imageUrl: null,
    },
    {
      id: "12",
      infoText:
        "Biologische Lebensmittel, kein Plastik, Mehrwegsysteme, Recyclingfähigkeit, regionaler Einkauf, weniger Fleisch – mehr Gemüse",
      videoUrl: null,
      imageUrl: null,
    },
    {
      id: "13",
      infoText:
        "Energieeffizienz, LED-Technik, Wärmepumpe, CO₂-Bilanzierung, Klimaneutralität durch Kompensation",
      videoUrl: null,
      imageUrl: null,
    },
    {
      id: "14",
      infoText:
        "Förderung der Biodiversität, Verzicht auf Chemikalien, biobasierte Reinigungsmittel, Gästeeinbindung bei ressourcenschonendem Verhalten",
      videoUrl: null,
      imageUrl: null,
    },
    {
      id: "15",
      infoText:
        "Förderung der Biodiversität, Verzicht auf Chemikalien, biobasierte Reinigungsmittel, Gästeeinbindung bei ressourcenschonendem Verhalten",
      videoUrl: null,
      imageUrl: null,
    },
  ],
  // awards: [
  //   {
  //     imageUrl: "/wiesenkietz/hessicher-gruender-preis.png",
  //     name: "Hessischer Gründerpreis 2020",
  //   },
  //   {
  //     imageUrl: "/wiesenkietz/WeltverbEsserer-Wettbewerb.png",
  //     name: "WeltverbEsserer Wettbewerb 2023",
  //   },
  // ],
  memberships: [
    {
      imageUrl: "/hotel-concorde/DEHOGA-Hessen.jpg",
      name: "DEHOGA Hessen",
    },
    {
      imageUrl: "/hotel-concorde/Genesys GmbH.jpg",
      name: "Genesys GmbH",
    },
  ],
  producerInfo: {
    text: `Das Hotel Concorde ist ein familiengeführtes Business-Boutique-Hotel in Bad Soden am Taunus.
Als unabhängiges Familienunternehmen wird es in fünfter Generation betrieben und vereint langjährige Tradition mit modernen Nachhaltigkeitsstandards. Es beschäftigt aktuell 17 Mitarbeitende, davon 73 % Frauen. Alle Führungspositionen sind weiblich besetzt.

Die Geschäftsführung liegt bei Andrea Weigand (Inhaberin und Geschäftsführerin) und Irene Marx (Geschäftsführerin). Beide setzen auf eine transparente, wertebasierte Unternehmensführung und treiben die nachhaltige Entwicklung des Hauses aktiv voran.

Das Unternehmen steht für Kontinuität, regionale Verwurzelung und transparente Unternehmensführung. Die Geschäftsführung wird aktiv in Nachhaltigkeitsfragen beraten. Mitarbeitende, Lieferanten und Gäste werden schrittweise in die nachhaltige Transformation eingebunden.

Ein interner Code of Conduct, Lieferantenrichtlinien sowie freiwillige Nachhaltigkeitsberichte bilden die Basis der Unternehmensstrategie.`,
    imageUrl: "/hotel-concorde/Irene Marx & Andrea Weigand.JPG",
    imageAlt: "Irene Marx & Andrea Weigand",
    contact: {
      name: "Hotel Concorde GmbH & Co. KG",
      street: "Am Bahnhof 1",
      postalCode: "65812",
      city: "Baden Soden am Taunus",
      phone: "+49(0)6196-209-0",
      email: "info@hotel-concorde.de ",
      web: "https://www.hotel-concorde.de/",
    },
  },
  social: {
    [SocialMedia.Instagram]: {
      type: SocialMedia.Instagram,
      value: "https://www.instagram.com/_hotelconcorde_/",
    },
    [SocialMedia.Facebook]: {
      type: SocialMedia.Facebook,
      value: "https://www.facebook.com/HotelConcordeTaunus",
    },
  },
};

type PublicPageInfos = Awaited<ReturnType<typeof getPublicPageInfo>>;
export const getServerSideProps: GetServerSideProps<{
  messages: any;
  infos: DigitalProductPassInformation;
  publicPageInfo: null | PublicPageInfos;
  openCO2: boolean;
  showVideo: boolean;
}> = async (context) => {
  const { locale, query } = context;

  let publicPageInfo: null | PublicPageInfos = null;
  if (ConcordeUebernachtungDPPInfos.publicId != null) {
    const user = await prisma.user.findUnique({
      where: {
        publicId: ConcordeUebernachtungDPPInfos.publicId,
      },
    });
    if (user) {
      publicPageInfo = await getPublicPageInfo(user.id);
    }
  }

  const openCO2 = "co2" in query;
  const showVideo = "video" in query;

  return {
    props: {
      infos: ConcordeUebernachtungDPPInfos,
      messages: await getMessages(Wiesenkietz.Messages, locale),
      publicPageInfo: publicPageInfo,
      openCO2: openCO2,
      showVideo: showVideo,
    },
  };
};

type WiesenkietzProps = InferGetServerSidePropsType<typeof getServerSideProps>;

function Wiesenkietz({
  infos,
  openCO2,
  publicPageInfo,
  showVideo: _showVideo,
}: WiesenkietzProps) {
  const tUtil = useTranslations("util");
  const t = useTranslations("DPPPage");
  const format = useFormatter();
  const [showVideo, setShowVideo] = useState(_showVideo);
  const [showCO2OffsetOverlay, setShowCO2OffsetOverlay] = useState(false);
  const [lightBoxInfo, setLightBoxInfo] = useState<
    | {
        src: string;
        alt?: string;
        isVideo?: boolean;
        isTextOnly?: boolean;
        imgProps?: {
          width?: number;
          height?: number;
        };
      }
    | undefined
  >(undefined);
  const [videoSize, setVideoSize] = useState({ height: 0, width: 0 });
  const videoContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // we need to set the video size after the lightbox is opened
    const t = setTimeout(() => {
      console.log("Setting video size");
      if (videoContainerRef.current) {
        const { height, width } =
          videoContainerRef.current.getBoundingClientRect();
        if (height === 0 || width === 0) {
          console.warn("Video container has no size, not setting video size");
          return;
        }
        setVideoSize({ height, width });
      }
    }, 300);
    return () => {
      clearTimeout(t);
    };
  }, [lightBoxInfo]);

  const [inViewProductRef, isInViewProduct, productEntry] = useInView({
    rootMargin: "-100px 0px -50% 0px",
  });
  // const [inViewSupplyChainRef, isInViewSupplyChain, supplyChainEntry] =
  //   useInView({
  //     rootMargin: "-50% 0px -50% 0px",
  //   });
  const [inViewCO2Ref, isInViewCO2, co2Entry] = useInView({
    rootMargin: "-50% 0px -50% 0px",
  });
  const [inViewSustainabilityRef, isInViewSustainability, sustainabilityEntry] =
    useInView({
      rootMargin: "-50% 0px -50% 0px",
    });
  const [inViewSDGRef, isInViewSDG, sdgEntry] = useInView({
    rootMargin: "-50% 0px -50% 0px",
  });
  const [inViewProducerRef, isInViewProducer, producerEntry] = useInView({
    rootMargin: "-50% 0px -50% 0px",
  });
  const [inViewCo2OffsetRef, isInViewCo2Offset, co2OffsetEntry] = useInView({
    rootMargin: "-50% 0px -50% 0px",
  });
  const [showIngredientsOrNutrition, setShowIngredientsOrNutrition] = useState<
    "ing" | "nut"
  >("ing");

  const navItemRefs = [
    useRef<HTMLDivElement>(null), // Product
    useRef<HTMLDivElement>(null), // Supply Chain
    useRef<HTMLDivElement>(null), // CO2 Footprint
    useRef<HTMLDivElement>(null), // Sustainability
    useRef<HTMLDivElement>(null), // SDG
    useRef<HTMLDivElement>(null), // Producer
    useRef<HTMLDivElement>(null), // CO2 Offset
  ];

  // Track which nav is active
  const activeNavIndex = isInViewProduct
    ? 0
    : isInViewCO2
    ? 2
    : isInViewSustainability
    ? 3
    : isInViewSDG
    ? 4
    : isInViewProducer
    ? 5
    : isInViewCo2Offset
    ? 6
    : -1;

  useEffect(() => {
    if (activeNavIndex === -1) return;
    navItemRefs[activeNavIndex]?.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNavIndex]);

  const stickyNavItems: {
    active: boolean;
    key: string;
    label: string;
    onClick: () => void;
    ref: RefObject<HTMLDivElement>;
  }[] = [
    {
      active: isInViewProduct,
      key: "product",
      label: t("product"),
      onClick: () => {
        productEntry?.target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      ref: navItemRefs[0],
    },
    // {
    //   active: isInViewSupplyChain,
    //   key: "supplyChain",
    //   label: t("supplyChain"),
    //   onClick: () => {
    //     supplyChainEntry?.target.scrollIntoView({
    //       // supplyChainRef.current?.scrollIntoView({
    //       behavior: "smooth",
    //       block: "start",
    //     });
    //   },
    //   ref: navItemRefs[1],
    // },
    {
      active: isInViewCo2Offset,
      key: "co2offset",
      label: "CO₂-Ausgleich",
      onClick: () => {
        co2OffsetEntry?.target.scrollIntoView({
          // sdgRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      ref: navItemRefs[6],
    },
    {
      active: isInViewCO2,
      key: "co2Footprint",
      label: t("co2Footprint"),
      onClick: () => {
        co2Entry?.target.scrollIntoView({
          // co2Ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      ref: navItemRefs[2],
    },
    {
      active: isInViewSustainability,
      key: "sustainability",
      label: t("sustainability"),
      onClick: () => {
        sustainabilityEntry?.target.scrollIntoView({
          // sustainabilityRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      ref: navItemRefs[3],
    },
    {
      active: isInViewSDG,
      key: "sdg",
      label: "SDG",
      onClick: () => {
        sdgEntry?.target.scrollIntoView({
          // sdgRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      ref: navItemRefs[4],
    },
    {
      active: isInViewProducer,
      key: "producer",
      label: "Hotel",
      onClick: () => {
        producerEntry?.target.scrollIntoView({
          // sdgRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      ref: navItemRefs[5],
    },
  ];

  const _openLink = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
    // if (link.startsWith("http")) {
    //   window.open(link, "_blank", "noopener,noreferrer");
    // } else {
    //   // Handle internal links or other cases
    //   console.warn("Unsupported link format:", link);
    // }
  };

  const allImgs: { src: string; alt: string }[] = [];
  const addImgFromProject = (p: SelectProjectForMap) => {
    if (p.ProjectPicture?.filename) {
      allImgs.push({
        src: p.ProjectPicture.filename,
        alt: p.id + " - " + p.name,
      });
    }
    // if (p.ProjectPhotos) {
    //   p.ProjectPhotos.forEach((pp) => allImgs.push(pp.filename));
    // }
    // if (p.SatellitePhotos) {
    //   p.SatellitePhotos.forEach((sp) => allImgs.push(sp.filename));
    // }
  };
  publicPageInfo?.nfts.forEach((n) => {
    if (n.picture_url) {
      allImgs.push({ src: n.picture_url, alt: n.id });
    }
    n.NFTProjectRelation.forEach((r) => {
      addImgFromProject(r.Project);
    });
  });
  publicPageInfo?.projects.forEach(addImgFromProject);

  return (
    <>
      <Accordion className={styles.main} allowMultiple>
        <div className={styles.pageHeader}>Digitaler Produkt Pass</div>
        <BaseModal
          isOpen={lightBoxInfo != null}
          overlayClassName={styles.lightboxOverlay}
          onRequestClose={() => {
            setLightBoxInfo(undefined);
          }}
          className={clsx(
            styles.lightBoxModal,
            lightBoxInfo?.isTextOnly && styles.hFitContent,
            lightBoxInfo?.imgProps != null && styles.hFitContent
          )}
        >
          {!lightBoxInfo?.isTextOnly && (
            <div className={styles.lightBoxContainer} ref={videoContainerRef}>
              {lightBoxInfo &&
                (lightBoxInfo.isVideo ? (
                  <video
                    controls
                    width={videoSize.width}
                    height={videoSize.height}
                  >
                    <source src={lightBoxInfo.src} />
                  </video>
                ) : lightBoxInfo.src != "" ? (
                  <NextImage
                    src={lightBoxInfo.src}
                    alt={lightBoxInfo.alt ?? ""}
                    fill={lightBoxInfo.imgProps == null}
                    width={lightBoxInfo.imgProps?.width}
                    height={lightBoxInfo.imgProps?.height}
                    unoptimized
                  />
                ) : null)}
            </div>
          )}
          {lightBoxInfo?.alt && (
            <div className={styles.alt}>{lightBoxInfo.alt}</div>
          )}
        </BaseModal>
        <FullScreenOverlayModal
          isOpen={showCO2OffsetOverlay}
          onRequestClose={() => {
            setShowCO2OffsetOverlay(false);
          }}
          publicPageInfos={publicPageInfo}
          publicId={infos.publicId}
        />
        <HotelVideoOverlay
          isOpen={showVideo}
          onClose={() => {
            setShowVideo(!showVideo);
          }}
        />
        <FabMenu items={stickyNavItems} />
        <section className={styles.heroSection}>
          <Ellipsis className={styles.heroSectionEllipsis} />
          <Ellipsis2 className={styles.heroSectionEllipsis2} />
          <div className={styles.heroHeader}>
            <NextImage
              src={"/hotel-concorde/hotel-concorde-logo.png"}
              alt={"Logo"}
              width={60}
              height={80}
            />
            <span className={styles.headerProductName}>
              {infos.product.name}
            </span>
          </div>
          {infos.heroSectionImages.length > 0 ? (
            <div className={styles.heroImageContainer}>
              <Carousel
                slideClassName={styles.slide}
                innerContainerClassName={styles.slideInnerContainer}
                prevNextClassName={styles.prevNext}
                containerClassName={styles.slideContainer}
                prevClassName={styles.prev}
                nextClassName={styles.next}
                slides={infos.heroSectionImages.map((s, i) => (
                  <NextImage
                    key={s.src}
                    src={s.src}
                    className={styles.heroImage}
                    fill
                    quality={80}
                    priority={i === 0}
                    alt={s.alt ?? ""}
                    onClick={() => {
                      setLightBoxInfo({
                        src: s.src,
                        alt: s.alt ?? "",
                      });
                    }}
                  />
                ))}
              />
            </div>
          ) : (
            <SkeletonImage height={"250px"} />
          )}
        </section>
        <AccordionItem
          key={"product"}
          ref={inViewProductRef}
          // className={styles.productContainer}
          buttonProps={{
            className: ({ isEnter }) =>
              clsx(styles.accBt, isEnter && styles.active),
          }}
          panelProps={{ className: styles.productContainer }}
          contentProps={{
            className: styles.itemContent,
          }}
          header={(props) => (
            <AccordionHeader state={props} header={t("product")} />
          )}
        >
          <C
            containerClassName={styles.productContainerCard}
            header={infos.product.name}
            text={infos.product.description}
          >
            <Separator m20 />
            <div className={styles.productInfos}>
              {infos.product.points?.map((p, index) => (
                <div key={index} className={styles.productInfoItem}>
                  <span className={styles.header}>{p.header}:</span>
                  <span>{p.text}</span>
                </div>
              ))}
            </div>
            {infos.oeko_plague && (
              <div className={styles.productOekoPlagueContainer}>
                <NextImage
                  width={260}
                  height={150}
                  className={styles.productOekoPlagueIcon}
                  alt={infos.oeko_plague.text}
                  src={infos.oeko_plague.icon}
                />
                <span className={styles.productOekoPlagueText}>
                  {infos.oeko_plague.text}
                </span>
              </div>
            )}
          </C>
          {infos.nutritionalValues && infos.ingredients && (
            <C>
              <div className={styles.tabBtContainer}>
                <button
                  className={clsx(
                    styles.tabBt,
                    showIngredientsOrNutrition === "ing" && styles.active
                  )}
                  onClick={() => {
                    setShowIngredientsOrNutrition("ing");
                  }}
                >
                  {t("ingredients")}
                </button>
                <button
                  className={clsx(
                    styles.tabBt,
                    showIngredientsOrNutrition === "nut" && styles.active
                  )}
                  onClick={() => {
                    setShowIngredientsOrNutrition("nut");
                  }}
                >
                  {t("nutritionalValues")}
                </button>
              </div>
              <div>
                <span
                  style={{
                    display:
                      showIngredientsOrNutrition === "ing" ? "block" : "none",
                  }}
                  className={styles.text}
                >
                  <span>{infos.ingredients.text}</span>
                </span>
                <span
                  style={{
                    display:
                      showIngredientsOrNutrition === "nut" ? "block" : "none",
                  }}
                  className={styles.text}
                >
                  {infos.nutritionalValues.text}
                </span>
              </div>
            </C>
          )}
          <C header={t("additionalInformation")}>
            {/*<span className={styles.subGreenHeader}>{tUtil("advantages")}</span>*/}
            <span className={styles.subGreenHeader}>
              Die besonderen Merkmale
            </span>
            <L
              items={infos.additionalInformation.pros.map((p) => ({ text: p }))}
            />
            {infos.additionalInformation.documents?.length > 0 && (
              <>
                <Separator m20 />
                <div className={styles.documentHeaderContainer}>
                  <span className={clsx(styles.subGreenHeader, styles.large)}>
                    {tUtil("documents")}
                  </span>
                  <span className={styles.documentSubHeader}>
                    Alle Dokumente haben eine digitale Signatur erhalten, die in
                    der Blockchain gespeichert wurde. Mithilfe dieser Signatur
                    kann ihre Echtheit verifiziert werden.
                  </span>
                </div>
                <div className={styles.documentContainer}>
                  {infos.additionalInformation.documents.map((doc) => (
                    <a
                      key={doc.name}
                      className={styles.documentItem}
                      href={
                        doc.url
                          ? doc.url
                          : doc.fileId
                          ? `/api/file/${doc.fileId}`
                          : "#"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DocumentSVG />
                      <span className={styles.text}>{doc.name}</span>
                    </a>
                  ))}
                </div>
              </>
            )}
          </C>
          {infos.productImages.length > 0 && (
            <div className={styles.productImageContainer}>
              {infos.productImages.map((s) => (
                <NextImage
                  key={s}
                  src={s}
                  className={styles.productImg}
                  alt={""}
                  width={150}
                  height={120}
                  onClick={() => {
                    console.log("Image clicked");
                    setLightBoxInfo({
                      src: s,
                      alt: "",
                    });
                  }}
                />
              ))}
            </div>
          )}
          {infos.cta && (
            <div className={styles.baseContainer}>
              <a
                href={infos.cta.url}
                target="_blank"
                className={styles.buyNowBt}
              >
                {infos.cta.text ?? t("buyNow")}
              </a>
            </div>
          )}
        </AccordionItem>

        {infos.supplyChain.length > 0 && (
          <div
            // ref={inViewSupplyChainRef}
            className={clsx(styles.baseContainer, styles.supplyChainContainer)}
          >
            {infos.supplyChain
              .sort((a, b) => a.pos - b.pos)
              .map((supplyChain, index) => (
                <C
                  key={index}
                  containerClassName={styles.supplyChainC}
                  header={supplyChain.pos + ". " + supplyChain.name}
                  onClick={
                    supplyChain.link != null
                      ? () => _openLink(supplyChain.link!)
                      : undefined
                  }
                >
                  <div className={styles.supplyChainContentContainer}>
                    {supplyChain.imageUrl ? (
                      <NextImage
                        className={styles.supplyChainImage}
                        src={supplyChain.imageUrl}
                        alt={supplyChain.name}
                        height={80}
                        width={80}
                      />
                    ) : (
                      <SkeletonImage height={"80px"} width={"80px"} />
                    )}
                    <span className={styles.text}>
                      {supplyChain.address.name ? (
                        <>
                          {supplyChain.address.name}
                          <br />
                        </>
                      ) : null}
                      {supplyChain.address.street}
                      <br />
                      {supplyChain.address.postalCode}{" "}
                      {supplyChain.address.city}
                    </span>
                  </div>
                </C>
              ))}
          </div>
        )}
        {infos.co2Offset && (
          <AccordionItem
            key={"co2offset"}
            initialEntered={openCO2}
            ref={inViewCo2OffsetRef}
            // className={clsx(styles.baseContainer, styles.co2OffsetContainer)}
            buttonProps={{
              className: ({ isEnter }) =>
                clsx(styles.accBt, isEnter && styles.active),
            }}
            panelProps={{
              className: clsx(styles.baseContainer, styles.co2OffsetContainer),
            }}
            contentProps={{
              className: styles.itemContent,
            }}
            header={(props) => (
              <AccordionHeader state={props} header={"CO₂-Ausgleich"} />
            )}
          >
            <C containerClassName={styles.co2OffsetContainerCard}>
              <NextImage
                className={styles.img}
                src={hgfPersonTree}
                height={100}
                width={130}
                alt=""
              />
              {publicPageInfo && (
                <span className={styles.co2OffsetValue}>
                  {format.number(publicPageInfo.total, FormatOptions.kg)}
                  <span className={styles.sUnit}>CO₂e</span>
                </span>
              )}
              <span className={styles.text}>{infos.co2Offset.text}</span>
              {allImgs.length > 0 && (
                <div className={styles.co2ProjectPictures}>
                  <span className={clsx(styles.subGreenHeader)}>
                    Auszug der unterstützen Klimaprojekte
                  </span>
                  <div className={styles.intProjContainer}>
                    <div className={styles.intProjImgList}>
                      <Carousel
                        innerContainerClassName={
                          styles.intProjectInnerContainer
                        }
                        slideClassName={styles.intProjectImgCo2OffsetSlide}
                        slides={allImgs.map((i, index) => (
                          // slides={[...allImgs, ...allImgs].map((i, index) => (
                          <NextImage
                            key={index}
                            className={styles.intProjImgContainer}
                            src={i.src}
                            alt={i.alt}
                            fill
                            // width={250}
                            // height={300}
                            onClick={() => {
                              // setLightBoxInfo(i);
                              // setIsLightBoxOpen(true);
                            }}
                            // unoptimized
                          />
                        ))}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.co2OffsetButtonContainer}>
                <Button
                  onClick={() => {
                    setShowCO2OffsetOverlay(true);
                  }}
                  className={styles.showCo2OffsetDataBt}
                  color="green"
                  disabled={!publicPageInfo}
                >
                  Daten anzeigen
                </Button>
              </div>
            </C>
            {/*</div>*/}
          </AccordionItem>
        )}
        <AccordionItem
          key={"co2"}
          ref={inViewCO2Ref}
          // className={clsx(styles.baseContainer, styles.co2FootprintContainer)}
          buttonProps={{
            className: ({ isEnter }) =>
              clsx(styles.accBt, isEnter && styles.active),
          }}
          panelProps={{
            className: clsx(styles.baseContainer, styles.co2FootprintContainer),
          }}
          contentProps={{
            className: styles.itemContent,
          }}
          header={(props) => (
            <AccordionHeader state={props} header={t("co2Footprint")} />
          )}
        >
          <C containerClassName={styles.co2FootprintContainerCard}>
            <div className={styles.footprintContainer}>
              {/*<FootprintSolid />*/}
              {/*<NextImage src={hgfPersonTree} height={100} width={130} alt="" />*/}
              <CO2Footprint />
              <div className={styles.footprintTextContainer}>
                <span
                  className={clsx(
                    styles.offset,
                    infos.co2Footprint.offsetRed && styles.red
                  )}
                >
                  {infos.co2Footprint.offset}&nbsp;
                  {infos.co2Footprint.offsetUnit}
                </span>
                {infos.co2Footprint.offsetHint && (
                  <span className={styles.offsetHint}>
                    {infos.co2Footprint.offsetHint}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.header}>{infos.co2Footprint.header}</div>
            {infos.co2Footprint.text ? (
              infos.co2Footprint.text.includes("<") ? (
                <span
                  className={styles.cText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(infos.co2Footprint.text, {
                      USE_PROFILES: { html: true },
                    }),
                  }}
                />
              ) : (
                <span className={styles.text}>{infos.co2Footprint.text}</span>
              )
            ) : null}
            {infos.co2Footprint.list && (
              <>
                {infos.co2Footprint.list.header && (
                  <span className={clsx(styles.text, styles.bold)}>
                    {infos.co2Footprint.list.header}:
                  </span>
                )}
                {infos.co2Footprint.list.items && (
                  <div className={styles.dotList}>
                    {infos.co2Footprint.list.items.map((item, index) => (
                      <div key={index} className={styles.dotItem}>
                        <div className={styles.dotIcon} />
                        <span className={styles.text}>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {infos.co2Footprint.additionalTexts &&
              infos.co2Footprint.additionalTexts.length > 0 && (
                <Separator m20 />
              )}
            {infos.co2Footprint.additionalTexts?.map((text, index) => (
              <Fragment key={index}>
                <span className={styles.subGreenHeader}>{text.header}</span>
                <span className={clsx(styles.cText)}>{text.text}</span>
              </Fragment>
            ))}
            {infos.co2Footprint.documents?.length > 0 && (
              <>
                <Separator m20 />
                <div className={styles.documentHeaderContainer}>
                  <span className={styles.subGreenHeader}>
                    {tUtil("documents")}
                  </span>
                  <span className={styles.documentSubHeader}>
                    Alle Dokumente haben eine digitale Signatur erhalten, die in
                    der Blockchain gespeichert wurde. Mithilfe dieser Signatur
                    kann ihre Echtheit verifiziert werden.
                  </span>
                </div>
                <div
                  className={clsx(
                    styles.documentContainer,
                    styles.documentColGap
                  )}
                >
                  {infos.co2Footprint.documents.map((doc) => (
                    <a
                      key={doc.name}
                      className={styles.documentItem}
                      href={
                        doc.url
                          ? doc.url
                          : doc.fileId
                          ? `/api/file/${doc.fileId}`
                          : "#"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DocumentSVG />
                      <span className={styles.text}>{doc.name}</span>
                    </a>
                  ))}
                </div>
              </>
            )}
          </C>
        </AccordionItem>
        <AccordionItem
          ref={inViewSustainabilityRef}
          // className={clsx(styles.baseContainer, styles.sustainabilityContainer)}
          key={"sustainability"}
          buttonProps={{
            className: ({ isEnter }) =>
              clsx(styles.accBt, isEnter && styles.active),
          }}
          panelProps={{
            className: clsx(
              styles.baseContainer,
              styles.sustainabilityContainer
            ),
          }}
          contentProps={{
            className: styles.itemContent,
          }}
          header={(props) => (
            <AccordionHeader state={props} header={t("sustainability")} />
          )}
        >
          <C containerClassName={styles.sustainabilityContainerCard}>
            <span className={clsx(styles.subGreenHeader, styles.large)}>
              Dienstleistung
            </span>
            <span className={styles.cText}>
              {infos.sustainability.product.text}
            </span>
            {infos.sustainability.product.additionalTexts?.map(
              (text, index) => (
                <div key={index} className={styles.contentItem}>
                  <div className={styles.contentHeaderContainer}>
                    <SustainabilityIcon
                      icon={text.icon}
                      className={styles.contentHeaderIcon}
                    />
                    <span className={styles.contentHeader}>{text.header}</span>
                  </div>
                  <span className={clsx(styles.cText, styles.n)}>
                    {text.text}
                  </span>
                </div>
              )
            )}
          </C>
          <C>
            <span className={clsx(styles.subGreenHeader, styles.large)}>
              {tUtil("company")}
            </span>
            {infos.sustainability.producer.text && (
              <span className={styles.cText}>
                {infos.sustainability.producer.text}
              </span>
            )}
            {infos.sustainability.producer.additionalTexts?.map(
              (text, index) => (
                <div key={index} className={styles.contentItem}>
                  <div className={styles.contentHeaderContainer}>
                    <SustainabilityIcon
                      icon={text.icon}
                      className={styles.contentHeaderIcon}
                    />
                    <span className={styles.contentHeader}>{text.header}</span>
                  </div>
                  <span className={clsx(styles.cText, styles.n)}>
                    {text.text}
                  </span>
                </div>
              )
            )}
            {infos.sustainability.esg && (
              <Accordion
                className={styles.esgAccordion}
                transition
                allowMultiple
              >
                <AccordionItem
                  key={"e"}
                  buttonProps={{
                    className: ({ isEnter }) =>
                      clsx(styles.accBt, isEnter && styles.active),
                  }}
                  header={(props) => (
                    <AccordionHeader state={props} header={"Umwelt (E)"} />
                  )}
                  panelProps={{ className: styles.itemPanel }}
                  contentProps={{ className: styles.itemContent }}
                >
                  {typeof infos.sustainability.esg.e === "string" ? (
                    <span className={styles.cText}>
                      {infos.sustainability.esg.e}
                    </span>
                  ) : (
                    <ul className={styles.list}>
                      {infos.sustainability.esg.e.map((item, index) => (
                        <li key={index} className={styles.listItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </AccordionItem>
                <AccordionItem
                  key={"s"}
                  buttonProps={{
                    className: ({ isEnter }) =>
                      clsx(styles.accBt, isEnter && styles.active),
                  }}
                  header={(props) => (
                    <AccordionHeader state={props} header={"Soziales (S)"} />
                  )}
                  panelProps={{ className: styles.itemPanel }}
                  contentProps={{ className: styles.itemContent }}
                >
                  {typeof infos.sustainability.esg.s === "string" ? (
                    <span className={styles.cText}>
                      {infos.sustainability.esg.s}
                    </span>
                  ) : (
                    <ul className={styles.list}>
                      {infos.sustainability.esg.s.map((item, index) => (
                        <li key={index} className={styles.listItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </AccordionItem>
                <AccordionItem
                  key={"g"}
                  buttonProps={{
                    className: ({ isEnter }) =>
                      clsx(styles.accBt, isEnter && styles.active),
                  }}
                  header={(props) => (
                    <AccordionHeader
                      state={props}
                      header={"Unternehmensführung (G)"}
                    />
                  )}
                  panelProps={{ className: styles.itemPanel }}
                  contentProps={{ className: styles.itemContent }}
                >
                  {typeof infos.sustainability.esg.g === "string" ? (
                    <span className={styles.cText}>
                      {infos.sustainability.esg.g}
                    </span>
                  ) : (
                    <ul className={styles.list}>
                      {infos.sustainability.esg.g.map((item, index) => (
                        <li key={index} className={styles.listItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </AccordionItem>
              </Accordion>
            )}
          </C>
        </AccordionItem>
        <AccordionItem
          ref={inViewSDGRef}
          // className={clsx(styles.baseContainer, styles.sdgContainer)}
          key={"sdg"}
          buttonProps={{
            className: ({ isEnter }) =>
              clsx(styles.accBt, isEnter && styles.active),
          }}
          panelProps={{
            className: clsx(styles.baseContainer, styles.sdgContainer),
          }}
          contentProps={{
            className: styles.itemContent,
          }}
          header={(props) => <AccordionHeader state={props} header={"SDG"} />}
        >
          <div
            style={{
              alignSelf: "center",
            }}
          >
            <div className={styles.sdgGrid}>
              {infos.sdgs.map((sdg) => (
                <div key={sdg.id} className={clsx(styles.sdgItemContainer)}>
                  <img
                    className={styles.sdgItemImg}
                    src={`/sdg/E-WEB-Goal-${sdg.id.padStart(2, "0")}.png`}
                    alt={tUtil(
                      `sdg.${sdg.id as keyof IntlMessages["util"]["sdg"]}`
                    )}
                    onClick={() => {
                      if (sdg.videoUrl) {
                        setLightBoxInfo({
                          src: sdg.videoUrl,
                          // alt: tUtil(
                          //   `sdg.${sdg.id as keyof IntlMessages["util"]["sdg"]}`
                          // ),
                          isVideo: true,
                        });
                      } else if (sdg.infoText != null) {
                        setLightBoxInfo({
                          src: `/sdg/E-WEB-Goal-${sdg.id.padStart(2, "0")}.png`,
                          alt: sdg.infoText,
                          imgProps: {
                            width: 200,
                            height: 200,
                          },
                          // isTextOnly: true,
                        });
                      }
                    }}
                  />
                  {sdg.videoUrl ? (
                    <div className={styles.sdgIcon}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="14"
                        viewBox="0 0 12 14"
                        fill="none"
                      >
                        <path
                          d="M2.28125 0.220513C1.81875 -0.0638115 1.2375 -0.0731849 0.765625 0.192393C0.29375 0.457971 0 0.957883 0 1.50154V12.4996C0 13.0432 0.29375 13.5432 0.765625 13.8087C1.2375 14.0743 1.81875 14.0618 2.28125 13.7806L11.2812 8.28159C11.7281 8.00976 12 7.52547 12 7.00056C12 6.47566 11.7281 5.99449 11.2812 5.71954L2.28125 0.220513Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  ) : sdg.infoText != null ? (
                    <div className={styles.sdgIcon}>
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </AccordionItem>
        <AccordionItem
          ref={inViewProducerRef}
          // className={clsx(styles.baseContainer, styles.producerContainer)}
          key={"hotel"}
          buttonProps={{
            className: ({ isEnter }) =>
              clsx(styles.accBt, isEnter && styles.active),
          }}
          panelProps={{
            className: clsx(styles.baseContainer, styles.producerContainer),
          }}
          contentProps={{
            className: styles.itemContent,
          }}
          header={(props) => <AccordionHeader state={props} header={"Hotel"} />}
        >
          <C
            text={infos.producerInfo.text}
            containerClassName={styles.producerContainerCard}
          />
          {infos.producerInfo.imageUrl ? (
            <div className={styles.producerImageContainer}>
              <NextImage
                className={styles.producerImage}
                src={infos.producerInfo.imageUrl}
                alt={t("producer")}
                quality={80}
                // height={250}
                // width={250}
                onClick={() => {
                  setLightBoxInfo({
                    src: infos.producerInfo.imageUrl!,
                    alt: infos.producerInfo.imageAlt,
                    // alt: t("producer"),
                  });
                }}
                fill
              />
            </div>
          ) : (
            <SkeletonImage
              style={{
                alignSelf: "stretch",
              }}
              height={"250px"}
            />
          )}
          {infos.awards && infos.awards?.length > 0 && (
            <C>
              <span className={clsx(styles.subGreenHeader, styles.large)}>
                {t("awards")}
              </span>
              <div className={styles.awardsList}>
                {infos.awards.map((award) => (
                  <div key={award.name}>
                    {award.imageUrl ? (
                      <img
                        src={award.imageUrl}
                        className={styles.awardImage}
                        alt={award.name ?? ""}
                        onClick={() => {
                          setLightBoxInfo({
                            src: award.imageUrl,
                            alt: award.name ?? "",
                          });
                        }}
                      />
                    ) : (
                      <SkeletonImage height={"200px"} width={"100px"} />
                    )}
                  </div>
                ))}
              </div>
            </C>
          )}
          {infos.memberships && infos.memberships?.length > 0 && (
            <C>
              <span className={clsx(styles.subGreenHeader, styles.large)}>
                {tUtil("ourPartners")}
              </span>
              <div className={styles.membershipList}>
                {infos.memberships.map((membership) => (
                  <div key={membership.name}>
                    <img
                      src={membership.imageUrl}
                      className={styles.membershipImage}
                      alt={membership.name ?? ""}
                      onClick={() => {
                        setLightBoxInfo({
                          src: membership.imageUrl,
                          alt: membership.name ?? "",
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </C>
          )}
          <C>
            <span className={clsx(styles.subGreenHeader, styles.large)}>
              {tUtil("contact")}
            </span>
            <span className={styles.text}>
              {infos.producerInfo.contact.name}
              <br />
              {infos.producerInfo.contact.street}
              <br />
              {infos.producerInfo.contact.postalCode}{" "}
              {infos.producerInfo.contact.city}
              <br />
              <br />
              <span className={clsx(styles.bold, styles.dark)}>Tel.:</span>{" "}
              <a href={`tel:${infos.producerInfo.contact.phone}`}>
                {infos.producerInfo.contact.phone}
              </a>
              <br />
              <span className={clsx(styles.bold, styles.dark)}>
                E-Mail:
              </span>{" "}
              <a href={`mailto:${infos.producerInfo.contact.email}`}>
                {infos.producerInfo.contact.email}
              </a>
              <br />
              <br />
              <span className={clsx(styles.bold, styles.dark)}>Web:</span>{" "}
              <a
                href={infos.producerInfo.contact.web}
                target="_blank"
                rel="noopener noreferrer"
              >
                {infos.producerInfo.contact.web}
              </a>
              <br />
              {infos.producerInfo.contact.shopUrl && (
                <>
                  <span className={clsx(styles.bold, styles.dark)}>
                    Onlineshop:
                  </span>{" "}
                  <a
                    href={infos.producerInfo.contact.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {infos.producerInfo.contact.shopUrl}
                  </a>
                </>
              )}
            </span>
            {infos.social != null &&
              Object.keys(infos.social ?? {}).length > 0 && (
                <>
                  <span className={clsx(styles.subGreenHeader, styles.large)}>
                    Social Media
                  </span>
                  <div className={styles.socialMediaContainer}>
                    {Object.entries(infos.social).map(([key, value]) => (
                      <SocialMediaIcon
                        type={key as SocialMedia}
                        data={infos.social}
                        key={key}
                        iconClassName={styles.clickable}
                        onClick={() => {
                          if (value) {
                            _openLink(value.value);
                          }
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
          </C>
        </AccordionItem>
      </Accordion>
    </>
  );
}

const HotelVideoOverlay = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <BaseModal
      className={styles.hotelVideoModal}
      onRequestClose={onClose}
      isOpen={isOpen}
      overlayClassName={styles.hotelVideoOverlay}
    >
      <div className={styles.hotelVideoContainer}>
        <video
          className={styles.video}
          src={"/hotel-concorde/andrea-weigand-hotel-concorde.mp4"}
          // autoPlay
          playsInline
          controls
        />
      </div>
    </BaseModal>
  );
};

const FabMenu = ({
  items,
}: {
  items: {
    active: boolean;
    key: string;
    label: string;
    onClick: () => void;
    ref: RefObject<HTMLDivElement>;
  }[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={styles.fabMenuContainer}>
      <div
        className={clsx(styles.fabMenuIconContainer, isOpen && styles.open)}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.iconWrapper}>
          <FontAwesomeIcon
            icon={faBars}
            size={"lg"}
            className={clsx(styles.fabIcon, styles.barsIcon)}
          />
          <FontAwesomeIcon
            icon={faX}
            size={"lg"}
            className={clsx(styles.fabIcon, styles.xIcon)}
          />
        </div>
      </div>
      <div className={clsx(styles.fabMenu, isOpen && styles.open)}>
        <div className={styles.fabMenuContent}>
          {items.map((item) => (
            <div
              className={styles.fabMenuItem}
              key={item.key}
              onClick={() => {
                item.onClick();
                setIsOpen(false);
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Ellipsis = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="102"
      height="254"
      viewBox="0 0 102 254"
      fill="none"
    >
      <path
        d="M240.644 32.9941C288.722 106.806 281.964 259.233 194.049 253.696C137.97 250.165 121.52 201.419 88.3739 156.046C46.2328 98.3611 -36.2374 104.894 19.9415 -13.6004C76.1203 -132.095 192.565 -40.8179 240.644 32.9941Z"
        fill="#F3F3F6"
      />
    </svg>
  );
};

const Ellipsis2 = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="71"
      height="325"
      viewBox="0 0 71 325"
      fill="none"
    >
      <path
        d="M-169.083 221.075C-217.161 147.263 -210.404 -5.16393 -122.488 0.372373C-66.4089 3.90387 -49.9597 52.6502 -16.8131 98.0228C25.3279 155.708 107.798 149.174 51.6193 267.669C-4.55957 386.164 -121.004 294.887 -169.083 221.075Z"
        fill="#F3F3F6"
      />
    </svg>
  );
};

const H = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    className?: string;
  }>
>(function _H({ children, className }, ref) {
  return (
    <div ref={ref} className={clsx(styles.hContainer, className)}>
      <div className={styles.hIcon} />
      <span className={styles.h}>{children}</span>
    </div>
  );
});

function C({
  children,
  containerClassName,
  onClick,
  header,
  text,
}: PropsWithChildren<{
  containerClassName?: string;
  onClick?: () => void;
  text?: string | { text: string };
  header?: string;
}>) {
  if (typeof text === "object" && text !== null) {
    text = text.text;
  }

  return (
    <div
      className={clsx(
        styles.cContainer,
        containerClassName,
        onClick && styles.clickable
      )}
      onClick={onClick}
    >
      {header && <span className={styles.cHeader}>{header}</span>}
      {text ? (
        text.includes("<") ? (
          <span
            className={styles.cText}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(text, {
                USE_PROFILES: { html: true },
              }),
            }}
          />
        ) : (
          <span className={clsx(styles.cText, styles.n)}>{text}</span>
        )
      ) : null}
      {children}
    </div>
  );
}

type LItem = {
  text: string;
};

function L({ items }: { items?: LItem[] }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={styles.lContainer}>
      {items.map((item, index) => (
        <div key={index} className={styles.lItem}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <g clipPath="url(#clip0_11_336)">
              <path
                d="M9 1.125C11.0886 1.125 13.0916 1.95468 14.5685 3.43153C16.0453 4.90838 16.875 6.91142 16.875 9C16.875 11.0886 16.0453 13.0916 14.5685 14.5685C13.0916 16.0453 11.0886 16.875 9 16.875C6.91142 16.875 4.90838 16.0453 3.43153 14.5685C1.95468 13.0916 1.125 11.0886 1.125 9C1.125 6.91142 1.95468 4.90838 3.43153 3.43153C4.90838 1.95468 6.91142 1.125 9 1.125ZM9 18C11.3869 18 13.6761 17.0518 15.364 15.364C17.0518 13.6761 18 11.3869 18 9C18 6.61305 17.0518 4.32387 15.364 2.63604C13.6761 0.948212 11.3869 0 9 0C6.61305 0 4.32387 0.948212 2.63604 2.63604C0.948212 4.32387 0 6.61305 0 9C0 11.3869 0.948212 13.6761 2.63604 15.364C4.32387 17.0518 6.61305 18 9 18ZM12.7723 7.14727C12.9902 6.9293 12.9902 6.5707 12.7723 6.35273C12.5543 6.13477 12.1957 6.13477 11.9777 6.35273L7.875 10.4555L6.02227 8.60273C5.8043 8.38477 5.4457 8.38477 5.22773 8.60273C5.00977 8.8207 5.00977 9.1793 5.22773 9.39727L7.47773 11.6473C7.6957 11.8652 8.0543 11.8652 8.27227 11.6473L12.7723 7.14727Z"
                fill="#65BC7B"
              />
            </g>
            <defs>
              <clipPath id="clip0_11_336">
                <rect width="18" height="18" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span className={styles.lText}>{item.text}</span>
        </div>
      ))}
    </div>
  );
}

function DocumentSVG({ className }: { className?: string }) {
  return (
    <svg
      className={clsx(styles.documentSVG, className)}
      xmlns="http://www.w3.org/2000/svg"
      // width="74"
      // height="100"
      viewBox="0 0 74 100"
      fill="none"
    >
      <g clipPath="url(#clip0_100_962)">
        <path d="M74 0H0V100H74V0Z" fill="url(#paint0_linear_100_962)" />
        <g opacity="0.35">
          <path
            d="M6.47179 13.677H21.274"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 15.1669H21.274"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 16.6567H21.274"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 18.1466H21.274"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 90.0327H21.274"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 91.5227H25.1995"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 25.9684H21.274"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 33.7903H67.5282"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 35.28H62.4615"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 36.77H63.2199"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 38.2598H65.9744"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 39.7498H63.2199"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 42.7295H65.2198"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 44.2192H67.5282"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 45.7092H62.4615"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 47.199H67.5282"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 48.689H65.9744"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 50.1787H64.4614"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 51.6687H66.6658"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 53.1584H13.8729"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 56.1382H62.8073"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 57.6282H67.5282"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 59.1179H65.2198"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 60.6079H66.6658"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.31195 63.5876H61.0155"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 62.0977H67.5282"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 65.0774H65.4949"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 66.5674H67.5282"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 68.0571H62.8073"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 69.5471H65.4949"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.31195 72.5269H67.3684"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 74.0166H64.4614"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 75.5066H66.3907"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 76.9963H61.982"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.31195 79.9761H30.229"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <g opacity="0.35">
          <path
            d="M6.47179 78.4863H66.6658"
            stroke="#414141"
            strokeMiterlimit="10"
          />
        </g>
        <path
          opacity="0.08"
          d="M67.5282 3.55334H49.2689V10.3211H67.5282V3.55334Z"
          fill="black"
          stroke="white"
          strokeMiterlimit="10"
        />
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_100_962"
          x1="36.9981"
          y1="100"
          x2="36.9981"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E8E8ED" />
          <stop offset="1" stopColor="#F0F0F0" />
        </linearGradient>
        <clipPath id="clip0_100_962">
          <rect width="74" height="100" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function CO2Footprint() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="140"
      height="109"
      viewBox="0 0 140 109"
      fill="none"
    >
      <g clipPath="url(#clip0_151_1892)">
        <path
          d="M134.524 67.0442C134.375 67.3296 134.221 67.61 134.061 67.8887C127.934 78.5627 113.681 85.1918 89.6448 81.6833C75.997 79.5964 70.185 87.2511 64.5568 95.0915C64.4605 95.2267 64.3643 95.3621 64.2666 95.4974C58.3729 103.719 52.5378 111.964 37.9477 109.223C22.4805 106.778 12.9347 94.2192 11.4577 80.8111C11.4397 80.648 11.4234 80.4833 11.4087 80.3203C10.5789 71.6273 13.1433 62.6259 19.686 55.8306C23.6375 51.727 29.253 49.5472 35.0489 49.6156C36.794 49.6482 38.5383 49.5188 40.2596 49.2293C40.6394 49.1624 41.0079 49.0891 41.3665 49.0059C48.7505 47.3186 52.153 42.3019 54.2399 36.1799C54.2889 36.0347 54.3377 35.8897 54.385 35.743C59.1768 21.143 56.961 0.651073 83.011 3.66564C106.322 5.38402 123.429 18.31 131.821 33.2225C131.904 33.3725 131.987 33.5225 132.069 33.6725C134.375 37.8103 136.021 42.2831 136.947 46.9289C136.999 47.1898 137.046 47.4507 137.09 47.7114C138.277 54.6371 137.499 61.3688 134.524 67.0442Z"
          fill="#F2F2F2"
        />
        <path
          d="M132.068 33.6722C130.396 35.6597 128.512 37.4586 126.449 39.0371C118.413 45.2 108.234 48.5421 98.193 49.2432C93.0325 49.6059 87.8467 49.2537 82.7828 48.1964C77.6307 47.117 72.7169 45.2161 67.9579 42.9955C63.3294 40.8353 58.837 38.3995 54.2401 36.1789C54.2891 36.0338 54.3379 35.8887 54.3852 35.742C59.3989 38.1549 64.3093 40.814 69.3813 43.1129C74.1598 45.2798 79.1064 47.0469 84.2795 47.9926C89.441 48.9296 94.7087 49.1359 99.9278 48.6056C109.941 47.598 120.128 43.9035 127.826 37.2941C129.272 36.0532 130.608 34.6907 131.82 33.2214C131.904 33.372 131.986 33.5223 132.068 33.6722Z"
          fill="white"
        />
        <path
          d="M97.2763 0.15332C97.2646 0.15332 97.2529 0.1537 97.2413 0.154455C84.8845 0.928597 75.205 11.2427 75.205 23.6358C75.205 36.0289 84.8845 46.3431 97.2412 47.117C97.315 47.1219 97.389 47.1116 97.4587 47.0866C97.5283 47.0616 97.592 47.0225 97.6458 46.9717C97.6994 46.9217 97.7422 46.8612 97.7713 46.7939C97.8004 46.7266 97.8152 46.6541 97.8149 46.5808V0.690661C97.8147 0.547998 97.7578 0.411261 97.6568 0.310502C97.5558 0.209744 97.419 0.153208 97.2763 0.15332Z"
          fill="#FF4E47"
        />
        <path
          d="M116.169 39.3165C115.986 39.3164 115.81 39.2436 115.68 39.1143L99.7032 23.1371C99.6389 23.0732 99.588 22.9972 99.5533 22.9135C99.5186 22.8298 99.5009 22.74 99.5011 22.6494V0.690697C99.5007 0.596462 99.5199 0.503171 99.5573 0.416691C99.5947 0.330212 99.6497 0.252415 99.7186 0.188198C99.7878 0.123229 99.8695 0.0731308 99.9588 0.041C100.048 0.00886919 100.143 -0.00461174 100.238 0.00138952C112.675 0.780347 122.418 11.1617 122.418 23.6357C122.418 29.3022 120.386 34.781 116.692 39.0773C116.629 39.1489 116.553 39.2071 116.468 39.2481C116.382 39.2891 116.289 39.3122 116.195 39.316C116.186 39.3163 116.178 39.3165 116.169 39.3165Z"
          fill="#CCCCCC"
        />
        <path
          d="M100.194 47.2716C100.017 47.2714 99.8477 47.2042 99.7189 47.0836C99.6499 47.0194 99.5949 46.9415 99.5574 46.8549C99.5199 46.7684 99.5007 46.675 99.5011 46.5807V26.7683C99.5011 26.6319 99.5416 26.4985 99.6174 26.3851C99.6932 26.2717 99.8009 26.1833 99.927 26.1311C100.053 26.0789 100.192 26.0653 100.325 26.0918C100.459 26.1184 100.582 26.1841 100.679 26.2805L114.621 40.2226C114.687 40.2889 114.739 40.3679 114.774 40.455C114.808 40.542 114.825 40.6352 114.823 40.7289C114.82 40.8226 114.799 40.9148 114.76 40.9999C114.72 41.0851 114.664 41.1614 114.595 41.2242C110.625 44.7977 105.567 46.9279 100.237 47.2701C100.222 47.2716 100.208 47.2716 100.194 47.2716Z"
          fill="#FF4E47"
        />
        <path
          d="M137.09 47.7112C134.853 51.6779 131.695 55.1766 128.079 57.9498C120.043 64.1126 109.865 67.4549 99.8233 68.156C94.6629 68.5187 89.4771 68.1665 84.4131 67.1092C79.2613 66.0298 74.3473 64.1289 69.5883 61.9083C64.7429 59.6471 60.0458 57.0825 55.2215 54.7787C50.4022 52.4767 45.4491 50.5137 40.2597 49.229C40.6396 49.1622 41.0081 49.0888 41.3667 49.0057C41.7058 49.0937 42.0438 49.185 42.3807 49.2796C52.5281 52.1164 61.4837 57.7065 71.0115 62.0261C75.7901 64.1931 80.7366 65.9602 85.9097 66.9058C91.0713 67.8428 96.339 68.0492 101.558 67.5188C111.571 66.5112 121.758 62.8167 129.457 56.2073C132.504 53.6026 135.043 50.457 136.947 46.929C136.999 47.1894 137.046 47.4503 137.09 47.7112Z"
          fill="white"
        />
        <path
          d="M36.3482 82.5389C46.3457 85.3333 55.1849 90.8015 64.5568 95.091C64.4605 95.2262 64.3644 95.3616 64.2667 95.4969C64.0286 95.3877 63.7923 95.2785 63.5558 95.1676C58.7104 92.9064 54.0133 90.3417 49.189 88.038C44.267 85.687 39.2063 83.69 33.8962 82.4067C26.5562 80.6387 18.9739 80.0994 11.4576 80.8106C11.4396 80.6475 11.4233 80.4828 11.4086 80.3198C19.7875 79.5289 28.2405 80.281 36.3482 82.5389Z"
          fill="white"
        />
        <path
          d="M76.5303 98.2697H5.43125C5.40631 98.2704 5.38149 98.2661 5.35825 98.257C5.33501 98.248 5.31383 98.2343 5.29594 98.2169C5.27806 98.1996 5.26385 98.1788 5.25415 98.1558C5.24444 98.1328 5.23944 98.1081 5.23944 98.0832C5.23944 98.0582 5.24444 98.0335 5.25415 98.0105C5.26385 97.9875 5.27806 97.9667 5.29594 97.9494C5.31383 97.932 5.33501 97.9183 5.35825 97.9093C5.38149 97.9002 5.40631 97.8959 5.43125 97.8966H76.5303C76.5789 97.8979 76.625 97.9181 76.6589 97.953C76.6928 97.9878 76.7118 98.0345 76.7118 98.0832C76.7118 98.1318 76.6928 98.1785 76.6589 98.2133C76.625 98.2482 76.5789 98.2684 76.5303 98.2697Z"
          fill="#CCCCCC"
        />
        <path
          d="M31.7686 84.4186L27.2621 83.8694C27.1339 83.8538 27.0106 83.8107 26.9005 83.7431C26.7904 83.6755 26.6962 83.585 26.6243 83.4777C26.5523 83.3704 26.5044 83.2488 26.4836 83.1213C26.4629 82.9938 26.4699 82.8633 26.5041 82.7388L27.7042 78.3746C27.7448 78.0493 27.8491 77.7352 28.0111 77.4502C28.1731 77.1652 28.3896 76.9149 28.6484 76.7136C28.9071 76.5123 29.203 76.3639 29.5191 76.2769C29.8351 76.19 30.1653 76.1661 30.4906 76.2067C30.8159 76.2473 31.13 76.3516 31.415 76.5136C31.7 76.6756 31.9503 76.8922 32.1516 77.1509C32.3529 77.4097 32.5013 77.7055 32.5883 78.0216C32.6752 78.3377 32.6991 78.6678 32.6585 78.9931L32.776 83.503C32.7793 83.6321 32.7548 83.7604 32.704 83.8792C32.6533 83.998 32.5776 84.1044 32.482 84.1913C32.3864 84.2782 32.2732 84.3434 32.1501 84.3826C32.0271 84.4218 31.897 84.434 31.7688 84.4184L31.7686 84.4186Z"
          fill="#FF4E47"
        />
        <path
          d="M94.983 72.0668H79.8544C79.6363 72.0668 79.4272 71.9802 79.273 71.826C79.1188 71.6718 79.0322 71.4627 79.0322 71.2446C79.0322 71.0265 79.1188 70.8174 79.273 70.6632C79.4272 70.509 79.6363 70.4224 79.8544 70.4224H94.983C95.2011 70.4224 95.4102 70.509 95.5644 70.6632C95.7186 70.8174 95.8052 71.0265 95.8052 71.2446C95.8052 71.4627 95.7186 71.6718 95.5644 71.826C95.4102 71.9802 95.2011 72.0668 94.983 72.0668Z"
          fill="#CCCCCC"
        />
        <path
          d="M75.3322 72.889C74.9907 72.889 74.6569 72.7877 74.3729 72.598C74.089 72.4083 73.8677 72.1386 73.737 71.8231C73.6063 71.5076 73.5721 71.1605 73.6388 70.8255C73.7054 70.4906 73.8698 70.183 74.1113 69.9415C74.3528 69.7 74.6604 69.5356 74.9953 69.469C75.3303 69.4023 75.6774 69.4365 75.9929 69.5672C76.3084 69.6979 76.5781 69.9192 76.7678 70.2031C76.9575 70.4871 77.0588 70.8209 77.0588 71.1624C77.0588 71.6203 76.8769 72.0595 76.5531 72.3833C76.2293 72.7071 75.7901 72.889 75.3322 72.889Z"
          fill="#FF4E47"
        />
        <path
          d="M94.983 77.0002H79.8544C79.6363 77.0002 79.4272 76.9135 79.273 76.7593C79.1188 76.6051 79.0322 76.396 79.0322 76.1779C79.0322 75.9599 79.1188 75.7507 79.273 75.5965C79.4272 75.4423 79.6363 75.3557 79.8544 75.3557H94.983C95.2011 75.3557 95.4102 75.4423 95.5644 75.5965C95.7186 75.7507 95.8052 75.9599 95.8052 76.1779C95.8052 76.396 95.7186 76.6051 95.5644 76.7593C95.4102 76.9135 95.2011 77.0002 94.983 77.0002Z"
          fill="#CCCCCC"
        />
        <path
          d="M75.3322 77.8223C74.9907 77.8223 74.6569 77.7211 74.3729 77.5313C74.089 77.3416 73.8677 77.072 73.737 76.7565C73.6063 76.441 73.5721 76.0938 73.6388 75.7589C73.7054 75.424 73.8698 75.1163 74.1113 74.8748C74.3528 74.6334 74.6604 74.4689 74.9953 74.4023C75.3303 74.3357 75.6774 74.3699 75.9929 74.5006C76.3084 74.6313 76.5781 74.8526 76.7678 75.1365C76.9575 75.4204 77.0588 75.7543 77.0588 76.0957C77.0588 76.5537 76.8769 76.9928 76.5531 77.3166C76.2293 77.6404 75.7901 77.8223 75.3322 77.8223Z"
          fill="#FF4E47"
        />
        <path
          d="M94.983 81.9335H79.8544C79.6363 81.9335 79.4272 81.8469 79.273 81.6927C79.1188 81.5385 79.0322 81.3294 79.0322 81.1113C79.0322 80.8932 79.1188 80.6841 79.273 80.5299C79.4272 80.3757 79.6363 80.2891 79.8544 80.2891H94.983C95.2011 80.2891 95.4102 80.3757 95.5644 80.5299C95.7186 80.6841 95.8052 80.8932 95.8052 81.1113C95.8052 81.3294 95.7186 81.5385 95.5644 81.6927C95.4102 81.8469 95.2011 81.9335 94.983 81.9335Z"
          fill="#CCCCCC"
        />
        <path
          d="M75.3322 82.7557C74.9907 82.7557 74.6569 82.6544 74.3729 82.4647C74.089 82.275 73.8677 82.0053 73.737 81.6898C73.6063 81.3743 73.5721 81.0272 73.6388 80.6922C73.7054 80.3573 73.8698 80.0497 74.1113 79.8082C74.3528 79.5667 74.6604 79.4023 74.9953 79.3357C75.3303 79.269 75.6774 79.3032 75.9929 79.4339C76.3084 79.5646 76.5781 79.7859 76.7678 80.0698C76.9575 80.3538 77.0588 80.6876 77.0588 81.0291C77.0588 81.487 76.8769 81.9262 76.5531 82.25C76.2293 82.5738 75.7901 82.7557 75.3322 82.7557Z"
          fill="#FF4E47"
        />
        <path
          d="M99.3296 76.1555H105.578C105.622 76.1555 105.664 76.1382 105.695 76.1074C105.726 76.0765 105.743 76.0347 105.743 75.9911V37.3605C105.743 37.3169 105.726 37.275 105.695 37.2442C105.664 37.2134 105.622 37.196 105.578 37.196C105.535 37.196 105.493 37.2134 105.462 37.2442C105.431 37.275 105.414 37.3169 105.414 37.3605V75.8268H99.3296C99.286 75.8268 99.2442 75.8441 99.2133 75.875C99.1825 75.9058 99.1652 75.9476 99.1652 75.9913C99.1652 76.0349 99.1825 76.0767 99.2133 76.1075C99.2442 76.1384 99.286 76.1557 99.3296 76.1557V76.1555Z"
          fill="#3F3D56"
        />
        <path
          d="M126.106 12.1122C125.688 12.1459 125.269 12.0549 124.903 11.8508C124.537 11.6466 124.24 11.3385 124.049 10.9654C123.858 10.5923 123.782 10.1709 123.83 9.75458C123.879 9.33823 124.05 8.94559 124.321 8.62632C124.593 8.30705 124.953 8.07547 125.356 7.96087C125.759 7.84628 126.187 7.85381 126.586 7.98251C126.985 8.11121 127.337 8.35531 127.597 8.68393C127.857 9.01256 128.014 9.41096 128.048 9.82875C128.071 10.1062 128.038 10.3854 127.953 10.6504C127.867 10.9153 127.73 11.1608 127.55 11.3729C127.37 11.5849 127.149 11.7594 126.901 11.8862C126.654 12.0131 126.383 12.0899 126.106 12.1122ZM126.079 11.7872C126.433 11.7587 126.77 11.6261 127.048 11.406C127.327 11.1859 127.533 10.8882 127.642 10.5507C127.751 10.2131 127.758 9.85082 127.661 9.50959C127.564 9.16837 127.368 8.86354 127.098 8.63366C126.828 8.40379 126.495 8.25918 126.143 8.21814C125.791 8.17709 125.434 8.24145 125.118 8.40307C124.803 8.56469 124.542 8.81632 124.369 9.12613C124.196 9.43594 124.119 9.79002 124.148 10.1436C124.186 10.6177 124.411 11.0572 124.773 11.3654C125.136 11.6736 125.605 11.8254 126.079 11.7872Z"
          fill="#CCCCCC"
        />
        <path
          d="M133.404 6.6737C134.124 6.6737 134.708 6.08977 134.708 5.36944C134.708 4.64912 134.124 4.06519 133.404 4.06519C132.683 4.06519 132.099 4.64912 132.099 5.36944C132.099 6.08977 132.683 6.6737 133.404 6.6737Z"
          fill="#CCCCCC"
        />
        <path
          d="M139.815 1.95707C140.193 1.95707 140.5 1.65029 140.5 1.27187C140.5 0.893444 140.193 0.58667 139.815 0.58667C139.436 0.58667 139.13 0.893444 139.13 1.27187C139.13 1.65029 139.436 1.95707 139.815 1.95707Z"
          fill="#CCCCCC"
        />
        <path
          d="M116.147 53.8299C115.916 54.1796 115.586 54.453 115.2 54.6154C114.813 54.7778 114.387 54.822 113.976 54.7423C113.564 54.6627 113.185 54.4627 112.887 54.1679C112.59 53.873 112.386 53.4964 112.302 53.0857C112.218 52.675 112.258 52.2486 112.416 51.8605C112.575 51.4724 112.844 51.14 113.192 50.9053C113.539 50.6706 113.948 50.5442 114.367 50.542C114.787 50.5398 115.197 50.662 115.547 50.8931C116.015 51.2034 116.342 51.6869 116.454 52.2374C116.566 52.7879 116.456 53.3606 116.147 53.8298V53.8299ZM112.882 51.6728C112.687 51.9687 112.583 52.316 112.585 52.6707C112.587 53.0254 112.694 53.3716 112.892 53.6655C113.091 53.9594 113.372 54.1878 113.701 54.3219C114.029 54.4559 114.39 54.4896 114.737 54.4186C115.085 54.3476 115.404 54.1752 115.653 53.9231C115.903 53.671 116.072 53.3505 116.139 53.0023C116.207 52.6541 116.169 52.2936 116.032 51.9666C115.894 51.6396 115.663 51.3607 115.367 51.1652C114.97 50.9035 114.486 50.81 114.02 50.9052C113.554 51.0003 113.145 51.2764 112.882 51.6728Z"
          fill="#CCCCCC"
        />
        <path
          d="M118.686 61.6249C119.406 61.6249 119.99 61.0409 119.99 60.3206C119.99 59.6003 119.406 59.0164 118.686 59.0164C117.966 59.0164 117.382 59.6003 117.382 60.3206C117.382 61.0409 117.966 61.6249 118.686 61.6249Z"
          fill="#CCCCCC"
        />
        <path
          d="M122.324 67.6883C122.703 67.6883 123.009 67.3815 123.009 67.0031C123.009 66.6246 122.703 66.3179 122.324 66.3179C121.946 66.3179 121.639 66.6246 121.639 67.0031C121.639 67.3815 121.946 67.6883 122.324 67.6883Z"
          fill="#CCCCCC"
        />
        <path
          d="M63.5192 18.2331C63.3327 17.8577 63.2616 17.4354 63.3151 17.0196C63.3685 16.6038 63.5441 16.2133 63.8195 15.8973C64.095 15.5813 64.4579 15.354 64.8625 15.2443C65.2671 15.1346 65.6951 15.1473 66.0925 15.2808C66.4898 15.4144 66.8386 15.6627 67.0948 15.9945C67.351 16.3263 67.5031 16.7266 67.5318 17.1449C67.5605 17.5631 67.4645 17.9804 67.256 18.3441C67.0475 18.7077 66.7358 19.0014 66.3604 19.1879C65.8569 19.4374 65.275 19.477 64.7424 19.298C64.2097 19.119 63.7699 18.736 63.5192 18.2331ZM67.0235 16.4917C66.8656 16.1741 66.6171 15.9104 66.3093 15.7341C66.0015 15.5577 65.6484 15.4765 65.2945 15.5009C64.9406 15.5252 64.6019 15.654 64.3212 15.8708C64.0405 16.0877 63.8304 16.3829 63.7174 16.7192C63.6045 17.0555 63.5939 17.4177 63.6868 17.76C63.7798 18.1024 63.9721 18.4094 64.2396 18.6424C64.5071 18.8754 64.8376 19.0239 65.1895 19.069C65.5413 19.1141 65.8987 19.0539 66.2163 18.8959C66.6417 18.6837 66.9656 18.3114 67.117 17.8607C67.2683 17.41 67.2347 16.9177 67.0235 16.4917Z"
          fill="#CCCCCC"
        />
        <path
          d="M56.6309 18.4764C57.3512 18.4764 57.9351 17.8925 57.9351 17.1722C57.9351 16.4519 57.3512 15.8679 56.6309 15.8679C55.9105 15.8679 55.3266 16.4519 55.3266 17.1722C55.3266 17.8925 55.9105 18.4764 56.6309 18.4764Z"
          fill="#CCCCCC"
        />
        <path
          d="M49.0251 17.6509C49.4035 17.6509 49.7103 17.3441 49.7103 16.9657C49.7103 16.5873 49.4035 16.2805 49.0251 16.2805C48.6467 16.2805 48.3399 16.5873 48.3399 16.9657C48.3399 17.3441 48.6467 17.6509 49.0251 17.6509Z"
          fill="#CCCCCC"
        />
        <path
          d="M4.60113 72.5667L5.89562 76.5716C5.89562 76.5716 4.72477 80.3306 5.27939 84.521C5.83401 88.7113 5.52582 88.8345 5.52582 88.8345C5.52582 88.8345 -0.0155383 88.5156 0.53908 90.18C1.0937 91.8444 6.51185 90.8688 6.51185 90.8688C6.51185 90.8688 7.55239 90.5886 8.0454 87.6307C8.53841 84.6728 9.7778 78.9755 9.7778 78.9755L8.61234 73.5974L4.60113 72.5667Z"
          fill="#FFB9B9"
        />
        <path
          d="M24.629 92.101L29.5588 91.9169L33.3178 91.3623L32.2085 95.0597L23.0876 95.3061L24.629 92.101Z"
          fill="#FFB9B9"
        />
        <path
          d="M31.7155 92.5941C31.7155 92.5941 31.6539 91.2384 31.0993 91.3C30.5447 91.3616 30.9145 90.807 31.4073 90.6838C31.9002 90.5606 33.3176 89.9443 33.3176 89.9443L38.9253 90.3141C38.9253 90.3141 42.561 93.5185 41.0821 95.2439C39.6032 96.9694 35.7825 95.4288 35.7825 95.4288L31.6538 95.4904C31.6538 95.4904 30.2982 93.2719 31.7155 92.5941Z"
          fill="#090814"
        />
        <path
          d="M33.934 87.8492L35.721 96.2299L39.4184 95.4904L37.3233 85.0146L35.1048 83.8438L33.934 87.8492Z"
          fill="#FFB9B9"
        />
        <path
          d="M12.6739 86.8013C12.6739 86.8013 2.32089 91.485 6.69721 96.2295C6.69721 96.2295 6.69721 98.448 11.319 98.0166C15.9407 97.5852 24.876 96.7844 25.3074 97.0306C25.7388 97.2767 27.0944 92.1624 25.9236 91.916C24.7527 91.6695 23.3355 91.3613 23.3355 91.3613C23.3355 91.3613 24.5676 91.1765 24.5676 90.1292C24.5676 90.1292 28.08 89.8827 29.1281 87.6648C30.1761 85.4469 31.1616 83.7826 31.1616 83.7826L33.5642 89.9441C33.5642 89.9441 36.3373 84.8294 38.4324 87.1094C38.4324 87.1094 36.0907 72.3815 34.3036 71.0259C32.5165 69.6702 31.2841 69.2388 29.6202 70.4713C27.9564 71.7037 26.4159 74.8464 26.4159 74.8464L12.6739 86.8013Z"
          fill="#090814"
        />
        <path
          d="M37.3849 93.7032C36.9542 94.0283 36.5428 94.3782 36.1528 94.7512C35.6598 95.2442 34.9819 94.9977 34.9206 94.7512C34.8593 94.5048 33.7497 94.9361 33.6265 96.7231C33.5033 98.5102 32.7639 100.112 34.7358 100.667C36.7077 101.221 36.646 102.824 36.646 102.824C38.2063 103.572 39.997 103.683 41.6375 103.132C44.4106 102.208 43.4245 101.222 43.4245 101.222L39.7272 95.121C39.7272 95.121 38.7406 93.2718 37.3849 93.7032Z"
          fill="#090814"
        />
        <path
          d="M27.1555 63.1382C27.1555 63.1382 33.0713 65.1717 33.6876 65.6025C34.3038 66.0334 37.0769 68.0669 35.7211 69.1149C34.3652 70.163 33.2567 70.8404 30.6064 70.0393C27.9561 69.2383 24.9988 66.7734 24.9988 66.7734L27.1555 63.1382Z"
          fill="#FFB9B9"
        />
        <path
          d="M10.4556 58.6398C10.4556 58.6398 10.5172 61.0431 10.2708 61.2895C10.0244 61.536 13.2287 68.8075 16.7412 65.6031C20.2538 62.3988 17.5423 61.1047 17.5423 61.1047C17.5423 61.1047 16.2482 58.7014 16.3101 58.455C16.372 58.2086 10.5793 58.455 10.5793 58.455L10.4556 58.6398Z"
          fill="#FFB9B9"
        />
        <path
          d="M5.03291 64.3708C5.03291 64.3708 2.69122 65.2335 2.69122 67.5752C2.69122 69.9169 3.55388 73.1212 4.1085 73.3061C4.66312 73.4909 8.36048 76.0174 9.03831 74.2303C9.71615 72.4433 5.03291 64.3708 5.03291 64.3708Z"
          fill="#FF4E47"
        />
        <path
          d="M21.2396 60.427C21.2396 60.427 23.5813 59.9956 25.2452 60.92C26.909 61.8444 28.3263 63.6931 28.3263 63.6931C28.3263 63.6931 26.7857 66.959 25.6766 67.452C24.5675 67.945 22.5338 65.4801 22.5338 65.4801L21.2396 60.427Z"
          fill="#FF4E47"
        />
        <path
          d="M11.4417 63.2615C11.4417 63.2615 13.7834 64.8638 14.77 64.4937C15.7566 64.1236 16.9885 62.5218 17.1117 62.3986C17.2349 62.2754 23.5205 74.9081 23.5205 74.9081C23.5205 74.9081 22.5961 79.5299 20.1312 80.8848C17.6663 82.2398 17.697 83.319 17.389 84.2433C17.0809 85.1675 13.1061 81.3162 13.1061 81.3162L10.5789 70.5948V64.0626L11.4417 63.2615Z"
          fill="#FF4E47"
        />
        <path
          d="M10.4249 60.8212C10.4249 60.8212 9.65458 60.6671 8.85282 61.3452C8.05106 62.0233 4.29238 64.5496 4.47763 64.7962C4.66288 65.0427 6.32701 68.555 6.69723 69.972C7.06744 71.389 7.6215 71.5742 7.6215 71.5742C7.6215 71.5742 8.91556 72.4985 8.73074 73.1765C8.54591 73.8544 7.74472 83.0977 8.85395 85.1313C9.96319 87.165 9.77822 88.9519 9.47018 89.4449C9.16214 89.9379 12.4281 88.0276 12.4281 88.0276C12.4281 88.0276 17.2963 84.0838 17.6661 82.913C17.9052 82.1083 18.0702 81.2834 18.1591 80.4487C18.1591 80.4487 15.0164 68.0008 14.0303 67.323C13.0443 66.6451 10.4249 60.8212 10.4249 60.8212Z"
          fill="#FF4E47"
        />
        <path
          d="M17.1947 60.7281C17.1947 60.7281 18.7131 59.7859 19.0217 60.0323C19.3303 60.2787 22.2261 60.2787 22.2877 60.5253C22.3493 60.7719 22.4725 66.6876 24.5679 68.2282C26.6633 69.7689 25.3072 72.85 25.3072 72.85L26.8478 75.3143C26.8478 75.3143 18.4672 83.3863 19.0217 80.6139C19.0526 80.4598 19.0217 80.4291 19.0217 80.4291C19.0217 80.4291 15.7552 66.4411 16.1863 65.7017C16.6174 64.9623 17.4018 61.6088 17.1947 60.7281Z"
          fill="#FF4E47"
        />
        <path
          opacity="0.1"
          d="M13.7832 60.9931C16.3017 60.9931 18.3432 58.9515 18.3432 56.4331C18.3432 53.9146 16.3017 51.873 13.7832 51.873C11.2648 51.873 9.22321 53.9146 9.22321 56.4331C9.22321 58.9515 11.2648 60.9931 13.7832 60.9931Z"
          fill="black"
        />
        <path
          d="M30.1134 66.0962L21.7327 68.9925C21.7327 68.9925 13.4136 70.9644 14.7079 73.2445C16.0023 75.5246 21.4863 71.6429 21.4863 71.6429L32.6466 70.3925C32.6466 70.3925 37.0766 66.6508 30.1134 66.0962Z"
          fill="#FFB9B9"
        />
        <path
          d="M13.7832 60.9196C16.3017 60.9196 18.3432 58.878 18.3432 56.3596C18.3432 53.8412 16.3017 51.7996 13.7832 51.7996C11.2648 51.7996 9.22321 53.8412 9.22321 56.3596C9.22321 58.878 11.2648 60.9196 13.7832 60.9196Z"
          fill="#FFB9B9"
        />
        <path
          d="M10.1518 51.8279L9.3083 51.5211C9.3083 51.5211 11.072 49.7574 13.5259 49.9108L12.8357 49.2206C12.8357 49.2206 14.5228 48.6073 16.0565 50.2176C16.8628 51.0641 17.7955 52.0587 18.3771 53.18H19.2804L18.9034 53.934L20.2229 54.6881L18.8685 54.5527C18.997 55.2036 18.9531 55.8769 18.7411 56.5056L18.4343 57.3491C18.4343 57.3491 17.2074 54.8953 17.2074 54.5885V55.3557C17.2074 55.3557 16.3638 54.6656 16.3638 54.2054L15.9037 54.7422L15.6737 53.8986L12.8364 54.7422L13.2966 54.052L11.5329 54.282L12.223 53.4385C12.223 53.4385 10.2293 54.4354 10.1525 55.279C10.0758 56.1225 9.079 57.196 9.079 57.196L8.61885 56.4293C8.61885 56.4293 7.92799 52.9782 10.1518 51.8279Z"
          fill="#090814"
        />
      </g>
      <defs>
        <clipPath id="clip0_151_1892">
          <rect
            width="139"
            height="108.976"
            fill="white"
            transform="translate(0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function SustainabilityIcon({
  className,
  icon,
}: {
  className?: string;
  icon?: string;
}) {
  let i;
  switch (icon) {
    case "tree":
      i = faTreeDeciduous;
      break;
    case "globe":
      i = faEarthAmericas;
      break;
    case "waterdroplet":
      i = faDroplet;
      break;
    case "apple":
      i = faAppleWhole;
      break;
    case "rotateRight":
      i = faRotateRight;
      break;
    case "heart":
      i = faHeart;
      break;
    case "leaf":
      i = faEnvira;
      break;
    case "jugBottle":
      i = faJugBottle;
      break;
    case "bedFront":
      i = faBedFront;
      break;
    case "dog":
      i = faDog;
      break;
  }

  if (!i) return null;

  return <FontAwesomeIcon icon={i} className={className} />;
}

function AccordionHeader({
  children,
  header,
  state,
}: PropsWithChildren<{
  header?: string;
  state: ItemState;
}>) {
  return (
    <>
      {header && <span className={styles.accordionHeader}>{header}</span>}
      {children}
      <FontAwesomeIcon
        icon={faChevronRight}
        className={clsx(styles.icon, state.state.isEnter && styles.rotateIcon)}
      />
    </>
  );
}

function Separator({
  className,
  style,
  m20,
}: { className?: string; m20?: boolean } & Pick<
  HTMLAttributes<HTMLDivElement>,
  "style"
>) {
  return (
    <div
      style={style}
      className={clsx(styles.separator, m20 && styles.m20, className)}
    />
  );
}

function FullScreenOverlayModal({
  children,
  overlayClassName,
  className,
  publicId,
  publicPageInfos,
  ...props
}: ReactModal.Props & {
  publicPageInfos: null | PublicPageInfos;
  publicId?: string;
}) {
  const t = useTranslations("PublicPage");
  const tUtil = useTranslations("util");
  const format = useFormatter();
  const router = useRouter();
  const [page, setPage] = useState(1);
  const { data: apiResult } = useGetPublicCo2OffsettingQuery({
    page,
    tf: "all",
    // sort: sortTable,
    pubId: publicId!,
  });

  if (!publicPageInfos) {
    return null;
  }

  const total = publicPageInfos.total;

  const sdgIds = new Set<string>();
  publicPageInfos.projects.forEach((p) =>
    p.SDG.forEach((s) => sdgIds.add(s.id))
  );
  publicPageInfos.nfts.forEach((n) =>
    n.NFTProjectRelation.forEach((r) =>
      r.Project.SDG.forEach((s) => sdgIds.add(s.id))
    )
  );

  const countries = new Map<
    string,
    {
      count: number;
      co2e: number;
      percentage?: number;
    }
  >();
  const updateCountryMap = (p: SelectProjectForMap) => {
    if (p?.Location?.country) {
      if (countries.has(p.Location.country)) {
        const c = countries.get(p.Location.country);
        if (c) {
          c.count += 1;
          c.co2e += p.carbon_credit_quantity_purchased_co2e_value;
          countries.set(p.Location.country, c);
        }
      } else {
        countries.set(p.Location.country, {
          count: 1,
          co2e: p.carbon_credit_quantity_purchased_co2e_value,
        });
      }
    }
  };

  publicPageInfos.projects.forEach(updateCountryMap);
  publicPageInfos.nfts.forEach((n) =>
    n.NFTProjectRelation.forEach((r) => {
      if (r.Project) updateCountryMap(r.Project);
    })
  );

  const co2eSum = Array.from(countries.values()).reduce(
    (acc, curr) => acc + curr.co2e,
    0
  );
  Array.from(countries.entries()).forEach(([country, c]) => {
    c.percentage = c.co2e / co2eSum;
    countries.set(country, c);
  });

  const allImgs: { src: string; alt: string }[] = [];
  const addImgFromProject = (p: SelectProjectForMap) => {
    if (p.ProjectPicture?.filename) {
      allImgs.push({
        src: p.ProjectPicture.filename,
        alt: p.id + " - " + p.name,
      });
    }
    // if (p.ProjectPhotos) {
    //   p.ProjectPhotos.forEach((pp) => allImgs.push(pp.filename));
    // }
    // if (p.SatellitePhotos) {
    //   p.SatellitePhotos.forEach((sp) => allImgs.push(sp.filename));
    // }
  };
  publicPageInfos.nfts.forEach((n) => {
    if (n.picture_url) {
      allImgs.push({ src: n.picture_url, alt: n.id });
    }
    n.NFTProjectRelation.forEach((r) => {
      addImgFromProject(r.Project);
    });
  });
  publicPageInfos.projects.forEach(addImgFromProject);

  const allStandardImgs: Record<string, string | null> = {};
  const addStandardImg = (standard: string, url: string | null) => {
    if (!standard || !url) return;
    if (standard.startsWith("[")) {
      console.log("Multiple standards found:", standard, url);
      const standards = JSON.parse(standard) as string[];
      const urls = JSON.parse(url) as string[];
      standards.forEach((s, i) => {
        if (s in allStandardImgs) return;
        allStandardImgs[s] = urls[i] ?? null;
      });
    } else {
      if (standard in allStandardImgs) return;
      allStandardImgs[standard] = url;
    }
  };
  publicPageInfos.nfts.forEach((nft) => {
    return nft.NFTProjectRelation.map((r) => r.Project).forEach((p) => {
      addStandardImg(p.standards_certification, p.standards_certification_url);
    });
  });
  publicPageInfos.projects.forEach((p) => {
    addStandardImg(p.standards_certification, p.standards_certification_url);
  });

  const tableRows: RowType[] = [];
  if (apiResult && apiResult.data.offsetting.length > 0) {
    for (let offsettingEle of apiResult.data.offsetting) {
      const row: RowType = [
        format.dateTime(
          new Date(offsettingEle.allocationDate),
          FormatOptions.date
        ),
        tUtil(offsettingEle.projectId ? "climateProject" : "climateNFT"),
        format.number(offsettingEle.offsetting, {
          style: "unit",
          unit: "kilogram",
        }),
        {
          value: offsettingEle.projectId ?? offsettingEle.nFTId,
          className: styles.link,
          href: offsettingEle.projectId
            ? "/project/" + offsettingEle.projectId
            : "/nft/" + offsettingEle.nFTId,
        },

        offsettingEle.CO2Code.purpose,
        offsettingEle.blockChainId
          ? {
              value: (
                <Button
                  color="green"
                  onClick={() => {
                    const w = window.open(
                      "https://bc.hellogreenmarket.com/" +
                        offsettingEle.blockChainId,
                      "_blank",
                      "noopener,noreferrer"
                    );
                    if (w) w.opener = null;
                  }}
                >
                  {tUtil("action.to_blockchain")}
                </Button>
              ),
            }
          : null,
      ];

      row._actionMenuItems = [
        {
          label: tUtil(
            offsettingEle.projectId ? "action.to_project" : "action.to_nft"
          ),
          action: () =>
            router.push(
              offsettingEle.projectId
                ? "/project/" + offsettingEle.projectId
                : "/nft/" + offsettingEle.nFTId
            ),
        },
      ];
      if (offsettingEle.blockChainId) {
        row._actionMenuItems.push({
          label: tUtil("action.to_blockchain_viewer"),
          external: true,
          action: () => {
            const w = window.open(
              "https://bc.hellogreenmarket.com/" + offsettingEle.blockChainId,
              "_blank",
              "noopener,noreferrer"
            );
            if (w) w.opener = null;
          },
        });
      }

      tableRows.push(row);
    }
  }

  return (
    <ReactModal
      closeTimeoutMS={305}
      overlayClassName={clsx(styles.fullscreenOverlay, overlayClassName)}
      className={clsx(styles.fullscreenOverlayContent, className)}
      preventScroll
      portalClassName={styles.fullscreenPortal}
      {...props}
    >
      <div className={styles.backHeader}>
        <div className={styles.backButton} onClick={props.onRequestClose}>
          <FontAwesomeIcon icon={faArrowLeftLong} size="xl" />
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.baseContainer}>
          <H>CO₂-Ausgleich Fußabdruck</H>
          <C>
            <div className={styles.heroCO2Container}>
              <NextImage src={hgfPersonTree} height={100} width={130} alt="" />
              <div className={styles.co2Content}>
                <span className={styles.co2Head}>
                  {t("carbon_offset")}
                  <br />
                  {t.rich("carbon_offset_all")}
                </span>
                <span className={styles.co2OffsetValue}>
                  {format.number(total, FormatOptions.kg)}
                  <span className={styles.sUnit}>CO₂e</span>
                </span>
              </div>
            </div>
          </C>
          <div
            className={clsx(styles.heroCO2Container, styles.heroBCContainer)}
          >
            <div className={clsx(styles.co2Content, styles.bcCO2Content)}>
              <div className={clsx(styles.co2Head, styles.bcHeader)}>
                <span className={styles.bcHeaderContent}>
                  {t("ga_blockchain_transactions")}
                </span>
                <div>
                  <BCLeaves />
                </div>
              </div>
              <span className={styles.text}>
                {t.rich("ga_blockchain_transactions_text")}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.baseContainer}>
          <H>{t("support_international_projects_raw")}</H>
          <div className={styles.intProjContainer}>
            <div className={styles.intProjImgList}>
              <Carousel
                innerContainerClassName={styles.intProjectInnerContainer}
                slideClassName={styles.intProjectImgSlide}
                slides={allImgs.map((i, index) => (
                  // slides={[...allImgs, ...allImgs].map((i, index) => (
                  <NextImage
                    key={index}
                    className={styles.intProjImgContainer}
                    src={i.src}
                    alt={i.alt}
                    fill
                    // width={250}
                    // height={300}
                    onClick={() => {
                      // setLightBoxInfo(i);
                      // setIsLightBoxOpen(true);
                    }}
                    // unoptimized
                  />
                ))}
              />
            </div>
          </div>
          <C>
            <span className={styles.text}>{t.rich("supported_standards")}</span>
            <div className={styles.intProjImgList}>
              <Carousel
                containerClassName={styles.standardImgContainer}
                innerContainerClassName={styles.standardInnerContainer}
                slideClassName={styles.standardImgSlide}
                prevNextClassName={styles.standardPrevNext}
                prevNextSize="xs"
                slides={Object.entries(allStandardImgs).map(
                  ([standard, url]) => (
                    <ProjectStandardImage
                      key={standard}
                      name={standard}
                      alt={standard}
                      className={clsx(styles.clickable, styles.standardImg)}
                      width={160}
                      height={160 * 0.5}
                      // fill
                      onClick={() => {
                        if (url) {
                          window.open(url, "_blank", "noopener,noreferrer");
                        }
                      }}
                    />
                  )
                )}
              />
            </div>
          </C>
        </div>
        {/* project imgs & standards end */}
        <div className={styles.baseContainer}>
          <H>{t("supportedClimateProjects_raw")}</H>
          <span className={styles.text}>{t.rich("overviewCountryAndSDG")}</span>
          <div className={styles.cpCountrySDGContainer}>
            <div className={styles.cpSDGListContainer}>
              {Array.from(sdgIds)
                .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                .map((i) => (
                  <SdgImage
                    key={i}
                    id={i}
                    width={120}
                    height={120}
                    alt={tUtil(`sdg.${i as keyof IntlMessages["util"]["sdg"]}`)}
                    className={styles.sdgImg}
                  />
                ))}
            </div>
            <div className={styles.cpCountryPercentContainer}>
              {Array.from(countries.entries()).map(([country, v]) => (
                <div key={country} className={styles.cpCountryItem}>
                  <div className={styles.cpCountryPercent}>
                    <span>
                      <span className={styles.n}>
                        {format.number((v.percentage ?? 0) * 100, {
                          style: "decimal",
                          maximumFractionDigits: 1,
                          signDisplay: "never",
                        })}
                      </span>
                      <span className={styles.p}>%</span>
                    </span>
                  </div>
                  <div className={styles.cpCountry}>{country}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* sdg & contry end */}
        <div className={styles.infoMapContainer}>
          <H>Projektübersicht</H>
          <MapBoxProvider>
            <MapBoxMapView
              nfts={publicPageInfos.nfts}
              projects={publicPageInfos.projects}
            />
          </MapBoxProvider>
        </div>
        <div className={styles.baseContainer}>
          <H>{t("overview_co2_reduction_header_raw")}</H>
          <Table
            className={styles.codeTable}
            tableClassName={styles.table}
            colHeader={[
              tUtil("allocationDate"),
              tUtil("type"),
              tUtil("offsetAmount"),
              tUtil("gaProjectId"),
              tUtil("purpose"),
              tUtil("blockchain"),
            ]}
            hideCountColumn
            // onSort={handleTableSort}
            rows={tableRows}
            rowClassName={styles.row}
            pagination={
              apiResult && (
                <TablePagination
                  count={apiResult._maxPage}
                  page={apiResult.page}
                  onPageChange={setPage}
                  itemCount={apiResult.count}
                  // itemType={tUtil("entries")}
                  // itemType={"Codes"}
                />
              )
            }
          />
        </div>
      </div>
    </ReactModal>
  );
}

function BCLeaves() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="68"
      height="80"
      viewBox="0 0 68 80"
      fill="none"
    >
      <g clipPath="url(#clip0_177_285)">
        <path
          d="M46.3739 31.5472C46.0736 20.9584 42.5827 13.2047 39.7071 8.5661C36.9365 4.09515 34.1972 1.58795 33.6077 1.07214C33.0126 1.58611 30.2365 4.09331 27.4659 8.4961C24.5958 13.0555 21.1435 20.6397 20.9906 30.873L20.6222 30.8674C20.7751 20.5402 24.2642 12.8878 27.1619 8.28609C30.3083 3.29933 33.4621 0.714761 33.4935 0.688971L33.6095 0.59502L33.7256 0.690813C33.7569 0.716604 36.8757 3.30117 40.0203 8.37451C42.9198 13.0518 46.4384 20.8681 46.7424 31.538L46.3739 31.549V31.5472Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M45.9281 32.0207L45.9152 31.5601C45.6168 21.0726 42.1627 13.4 39.3184 8.80927C36.9143 4.92966 34.5232 2.53851 33.6095 1.69111C32.6847 2.53667 30.2659 4.92044 27.8601 8.74295C25.0213 13.2526 21.6059 20.7539 21.4567 30.8822L21.4493 31.3427L20.1598 31.3243L20.1672 30.8638C20.3219 20.4297 23.8478 12.6944 26.7769 8.04293C29.9638 2.97879 33.1729 0.357382 33.2042 0.329749L33.6113 0L34.0185 0.333433C34.0498 0.359224 37.2349 3.00274 40.4126 8.13135C43.3417 12.8565 46.8971 20.7521 47.2029 31.5251L47.2158 31.9857L45.9263 32.0225L45.9281 32.0207Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M34.2691 75.1146H33.9006V45.3672L33.8785 44.4295L33.9135 44.3798C35.9934 41.3936 38.4821 38.4461 41.3117 35.6147C41.9638 34.9626 42.6528 34.3289 43.3639 33.7283C53.5142 25.1383 67.08 23.8488 67.2163 23.8377L67.4355 23.8193L67.4153 24.0385C67.4024 24.1933 65.9268 39.6564 55.6383 49.945C52.1621 53.4248 47.4019 56.4976 44.215 58.5479V70.487H43.8465V58.3471L43.9313 58.2937C47.1035 56.2526 51.8968 53.1669 55.3767 49.6852C64.8952 40.1667 66.8018 26.1644 67.0229 24.2301C65.2212 24.4364 52.9469 26.1017 43.5997 34.012C42.896 34.6052 42.2144 35.2334 41.5696 35.8763C38.7713 38.6746 36.3065 41.5907 34.247 44.5419L34.2654 45.3635V75.1146H34.2691Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M34.7296 75.5751H33.4401V45.3672L33.4143 44.2895L33.534 44.1163C35.6304 41.1081 38.1358 38.1385 40.9856 35.2905C41.6451 34.631 42.3452 33.9881 43.0673 33.3783C53.3337 24.6888 67.0395 23.3919 67.1758 23.3809L67.9421 23.3127L67.874 24.079C67.8592 24.2356 66.3726 39.8628 55.9643 50.271C52.501 53.7361 47.844 56.761 44.6773 58.8003V70.9494H43.3878V58.0966L43.6844 57.9068C46.8401 55.8767 51.6094 52.8077 55.0543 49.361C63.7936 40.6217 66.0539 27.92 66.4942 24.7643C63.5964 25.1696 52.4384 27.1407 43.9018 34.3639C43.2073 34.9497 42.5349 35.5687 41.8993 36.2024C39.1582 38.9435 36.7413 41.7952 34.7149 44.6819L34.7296 45.3524V75.5751Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M42.6472 70.417C42.6472 71.1815 43.268 71.8023 44.0325 71.8023C44.797 71.8023 45.4178 71.1815 45.4178 70.417C45.4178 69.6525 44.797 69.0317 44.0325 69.0317C43.268 69.0317 42.6472 69.6525 42.6472 70.417Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M36.5976 77.4873C36.5976 76.102 35.4702 74.9764 34.0867 74.9764C32.7032 74.9764 31.5758 76.1038 31.5758 77.4873C31.5758 78.8708 32.7032 79.9982 34.0867 79.9982C35.4702 79.9982 36.5976 78.8708 36.5976 77.4873C36.5976 78.8726 36.5976 76.1038 36.5976 77.4873ZM32.1635 77.4873C32.1635 76.4281 33.0256 75.5659 34.0848 75.5659C35.1441 75.5659 36.0062 76.4281 36.0062 77.4873C36.0062 78.5466 35.1441 79.4087 34.0848 79.4087C33.0256 79.4087 32.1635 78.5466 32.1635 77.4873C32.1635 78.5466 32.1635 76.4281 32.1635 77.4873Z"
          fill="#66BC7B"
        />
        <path
          d="M36.5976 77.4873C36.5976 76.102 35.4702 74.9764 34.0867 74.9764C32.7032 74.9764 31.5758 76.1038 31.5758 77.4873C31.5758 78.8708 32.7032 79.9982 34.0867 79.9982C35.4702 79.9982 36.5976 78.8708 36.5976 77.4873ZM36.5976 77.4873C36.5976 76.1038 36.5976 78.8726 36.5976 77.4873ZM32.1635 77.4873C32.1635 76.4281 33.0256 75.5659 34.0848 75.5659C35.1441 75.5659 36.0062 76.4281 36.0062 77.4873C36.0062 78.5466 35.1441 79.4087 34.0848 79.4087C33.0256 79.4087 32.1635 78.5466 32.1635 77.4873ZM32.1635 77.4873C32.1635 76.4281 32.1635 78.5466 32.1635 77.4873Z"
          stroke="#66BC7B"
        />
        <path
          d="M24.1371 70.4852H23.7686V58.5461C18.8666 55.3849 15.0275 52.4282 12.3582 49.7589C2.07337 39.4704 0.595953 24.0072 0.583058 23.8524L0.564636 23.6332L0.783855 23.6535C0.912807 23.6645 13.8835 24.8951 23.9455 32.9749C24.9126 33.7504 25.8355 34.5757 26.6903 35.4305C29.5752 38.3153 32.1081 41.3273 34.2193 44.3798L33.9153 44.5898C31.8189 41.5557 29.3007 38.5622 26.4306 35.6921C25.585 34.8465 24.6713 34.0286 23.7152 33.2622C14.4638 25.8346 2.73287 24.2448 0.97544 24.044C1.1965 25.9783 3.10499 39.9807 12.6216 49.4992C15.2873 52.1648 19.1337 55.1233 24.0542 58.2918L24.1389 58.3453V70.4852H24.1371Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M24.5976 70.9457H23.3081V58.7966C18.4798 55.6686 14.6867 52.7377 12.034 50.085C1.62574 39.6749 0.137269 24.0495 0.124374 23.893L0.0562134 23.1266L0.822557 23.1948C0.953351 23.2058 14.0659 24.453 24.231 32.6156C25.2092 33.4004 26.1469 34.2386 27.0127 35.1044C29.9178 38.0095 32.4692 41.0417 34.5933 44.1182L34.8549 44.4977L33.7938 45.2308L33.5322 44.8514C31.4505 41.8394 28.9507 38.868 26.099 36.0181C25.2663 35.1855 24.3655 34.3786 23.4223 33.6233C14.9907 26.8533 4.319 24.9725 1.50416 24.5801C1.94444 27.7357 4.20479 40.4357 12.9441 49.1768C15.5857 51.8185 19.4082 54.7567 24.301 57.9068L24.5958 58.0966V70.9494L24.5976 70.9457Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M22.5675 70.417C22.5675 71.1815 23.1883 71.8023 23.9528 71.8023C24.7173 71.8023 25.3381 71.1815 25.3381 70.417C25.3381 69.6525 24.7173 69.0317 23.9528 69.0317C23.1883 69.0317 22.5675 69.6525 22.5675 70.417Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M51.0163 61.5783H44.0326V61.9467H51.0163V61.5783Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M52.4107 63.3321C51.5449 63.3321 50.8412 62.6284 50.8412 61.7625C50.8412 60.8967 51.5449 60.193 52.4107 60.193C53.2765 60.193 53.9802 60.8967 53.9802 61.7625C53.9802 62.6284 53.2765 63.3321 52.4107 63.3321C53.2765 63.3321 51.5449 63.3321 52.4107 63.3321ZM52.4107 60.5614C51.7475 60.5614 51.2096 61.0993 51.2096 61.7625C51.2096 62.4257 51.7475 62.9636 52.4107 62.9636C53.0739 62.9636 53.6118 62.4257 53.6118 61.7625C53.6118 61.0993 53.0739 60.5614 52.4107 60.5614C53.0739 60.5614 51.7475 60.5614 52.4107 60.5614Z"
          fill="#66BC7B"
        />
        <path
          d="M52.4107 63.3321C51.5449 63.3321 50.8412 62.6284 50.8412 61.7625C50.8412 60.8967 51.5449 60.193 52.4107 60.193C53.2765 60.193 53.9802 60.8967 53.9802 61.7625C53.9802 62.6284 53.2765 63.3321 52.4107 63.3321ZM52.4107 63.3321C51.5449 63.3321 53.2765 63.3321 52.4107 63.3321ZM52.4107 60.5614C51.7475 60.5614 51.2096 61.0993 51.2096 61.7625C51.2096 62.4257 51.7475 62.9636 52.4107 62.9636C53.0739 62.9636 53.6118 62.4257 53.6118 61.7625C53.6118 61.0993 53.0739 60.5614 52.4107 60.5614ZM52.4107 60.5614C51.7475 60.5614 53.0739 60.5614 52.4107 60.5614Z"
          stroke="#66BC7B"
        />
        <path
          d="M24.0468 61.5783H17.0631V61.9467H24.0468V61.5783Z"
          fill="#66BC7B"
          stroke="#66BC7B"
        />
        <path
          d="M15.6668 63.3321C16.5326 63.3321 17.2363 62.6284 17.2363 61.7625C17.2363 60.8967 16.5326 60.193 15.6668 60.193C14.8009 60.193 14.0972 60.8967 14.0972 61.7625C14.0972 62.6284 14.8009 63.3321 15.6668 63.3321C16.5326 63.3321 14.8009 63.3321 15.6668 63.3321ZM15.6668 60.5614C16.3299 60.5614 16.8679 61.0993 16.8679 61.7625C16.8679 62.4257 16.3299 62.9636 15.6668 62.9636C15.0036 62.9636 14.4657 62.4257 14.4657 61.7625C14.4657 61.0993 15.0036 60.5614 15.6668 60.5614C16.3299 60.5614 15.0036 60.5614 15.6668 60.5614Z"
          fill="#66BC7B"
        />
        <path
          d="M15.6668 63.3321C16.5326 63.3321 17.2363 62.6284 17.2363 61.7625C17.2363 60.8967 16.5326 60.193 15.6668 60.193C14.8009 60.193 14.0972 60.8967 14.0972 61.7625C14.0972 62.6284 14.8009 63.3321 15.6668 63.3321ZM15.6668 63.3321C14.8009 63.3321 16.5326 63.3321 15.6668 63.3321ZM15.6668 60.5614C16.3299 60.5614 16.8679 61.0993 16.8679 61.7625C16.8679 62.4257 16.3299 62.9636 15.6668 62.9636C15.0036 62.9636 14.4657 62.4257 14.4657 61.7625C14.4657 61.0993 15.0036 60.5614 15.6668 60.5614ZM15.6668 60.5614C15.0036 60.5614 16.3299 60.5614 15.6668 60.5614Z"
          stroke="#66BC7B"
        />
      </g>
      <defs>
        <clipPath id="clip0_177_285">
          <rect
            width="68"
            height="80"
            fill="white"
            // transform="translate(0.0562134)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

Wiesenkietz.Messages = ["DPPPage", "PublicPage", "ProjectId"];
Wiesenkietz.Layout = {
  hideHeader: true,
  containerClassName: styles.layoutContainer,
};

export default Wiesenkietz;
